// File meant to be the source for all the api calls
import axios from 'axios';
import { cacheManager } from '../utils/CacheManager';
import { INTEGRATION_UPGRADES } from '../utils/legacySubsectionInstructions';
import { discardSessionReplay, getActiveReplayId, noteApiCall } from './replay';
import { getDeviceFingerprint, isCX } from './DeviceInfo';
import { sendNativeMessage } from './nativeRecorder';
import {
  DEMO_ACCOUNT_SESSION_KEY,
  getSessionEmail,
  getSessionKey,
  INVALID_DEMO_ENDPOINTS,
  removeSessionKey,
  storeLocalData,
  storeSessionEmail,
  storeSessionKey,
  SUBSCRIPTION_REQUIRED_ENDPOINTS,
} from './Common.js';

const BACKEND_TARGETS = {
  prod: 'https://api.app.comprehendpt.com',
  dev: 'https://api.dev.comprehendpt.com',
  local: 'http://127.0.0.1:5001',
};
// A development environment beside dev (LambdaAPI scripts/dev_env.py, e.g.
// coder): its web app (<name>.comprehendpt.com) is built with
// REACT_APP_DEFAULT_BACKEND_ENV=<name> and talks to api.<name>.comprehendpt.com.
const BUILD_BACKEND_ENV = String(process.env.REACT_APP_DEFAULT_BACKEND_ENV || '');
if (/^[a-z][a-z0-9]{1,15}$/.test(BUILD_BACKEND_ENV) && !BACKEND_TARGETS[BUILD_BACKEND_ENV]) {
  BACKEND_TARGETS[BUILD_BACKEND_ENV] = `https://api.${BUILD_BACKEND_ENV}.comprehendpt.com`;
}

const BACKEND_STORAGE_KEY = 'backend_env';

/**
 * How many recent API calls to keep for bug reports / replay inspection.
 * 100 rather than 30: a replay is often watched minutes after the call that
 * broke it, and the interesting call is usually several requests back.
 */
const API_LOG_LIMIT = 100;
// Build-time override for local QA builds only (`REACT_APP_DEFAULT_BACKEND_ENV=dev
// python3 build.py` in the extension): a non-dev account can't flip the
// switch in-app, so the build itself points at dev. Shipped builds leave it
// unset → prod.
const DEFAULT_BACKEND_ENV = BUILD_BACKEND_ENV && BUILD_BACKEND_ENV !== 'prod' && BACKEND_TARGETS[BUILD_BACKEND_ENV]
  ? BUILD_BACKEND_ENV
  : 'prod';

function apiLogBodyBytes(value) {
  try {
    const json = JSON.stringify(value);
    return json === undefined ? null : new TextEncoder().encode(json).length;
  } catch (e) {
    return null;
  }
}

/**
 * Calls that never reach a session replay at all.
 *
 * Credentials: the replay pipe must not record its own traffic, and
 * replay-admin plus impersonation responses carry temporary tokens.
 */
// Golden Hour is company metrics and finance (~1 MB per load); keep it out of replays.
const REPLAY_EXCLUDED_PREFIXES = ['/v1/replay/', '/v1/admin/replay/', '/goldenhour/'];
const REPLAY_EXCLUDED_PATHS = ['/v1/admin/impersonateUser'];

/**
 * Calls that appear on the timeline, but without their bodies.
 *
 * An audio upload's request body IS the recording — megabytes of base64 that
 * would be cloned into a replay chunk, are not readable in the viewer, and are
 * the patient's voice besides. The call itself is worth seeing: when it went,
 * how long it took, what came back. So the metadata rides and the payload does
 * not.
 */
const REPLAY_BODYLESS_PREFIXES = ['/v1/conversation/audio/', '/v1/vendorApps/data/'];
const REPLAY_BODYLESS_PATHS = [
  '/v1/transcribe',
  // Same base64 audio, different feature: the ambient loop posts one of these
  // every ~15s, which makes it the most frequent audio body in the product.
  '/v1/synapse/ambient/chunk',
  '/v1/synapse/schedule/parse',
];

function matches(path, prefixes, paths) {
  return prefixes.some((prefix) => path.startsWith(prefix)) || paths.includes(path);
}

function excludedFromReplay(path) {
  return matches(path, REPLAY_EXCLUDED_PREFIXES, REPLAY_EXCLUDED_PATHS);
}

/** Excluded calls are bodyless too — they are not recorded at all. */
function bodylessInReplay(path) {
  return (
    excludedFromReplay(path) ||
    matches(path, REPLAY_BODYLESS_PREFIXES, REPLAY_BODYLESS_PATHS)
  );
}

function replayBodySnapshot(value) {
  try {
    const json = JSON.stringify(value);
    return json === undefined ? null : JSON.parse(json);
  } catch (e) {
    return { __unserializable: true };
  }
}

const FRONTEND_FEATURE_DEFAULTS = {
  // Chat can read and change the open note and run the Instruction Assistant
  // (COM-536), asking before every change.
  agentic_chat: false,
  sync_cal: false,
  get_all_notes: true,
  raintree_note_capture: false,
  user_note_rules: true,
  new_rt_integration: false,
  new_prompt_integration: false,
  new_webpt_soap2_integration: false,
  new_webpt_integration: false,
  webpt2_chrome_freeze_fix: false,
  // Show the onboarding checklist regardless of account age (our own test
  // accounts predate the launch-date gate in components/Onboarding).
  force_onboarding_checklist: false,
  // CORA: route through NewCoraHealthInject (ROM WNL/WFL, STG goal mapping).
  cora_rom: false,
  // PT Everywhere: read custom note templates (Open Answer and Table sections).
  pte_custom_note_types: false,
  // Learn++: whole-note async learn flow. Off by default — legacy
  // per-section /v1/template/learn — until enabled per user server-side.
  learn_plus: false,
  compliance_sign_off_gate: false,
  // Comprehend Live ambient agent panel (docs/comprehend-live.md).
  // On by default for branch/QA builds; backend per-user `features` wins on
  // conflict, so production rollout can still be gated server-side.
  comprehend_live: false,
  // MultiRecord++ V0.5 tray: vertical timeline + auto-switcher + schedule
  // loader, no ambient brain (docs/comprehend-live-v05.md). Shares the tray
  // surface with ambient; ambient wins if both are on.
  multirecord_tray: false,
  // Sliding-window segmentation and the Multi Recorder page's attribution review.
  multirecord_context_and_error_prevention: false,
  // Scorecard: relabels the compliance surface as "Scorecard" and swaps the
  // backend grading directive to conversion coaching. Off = compliance.
  scorecard: false,
  dictation: false,
  // Vendor Apps tab + iframe host (docs/vendor-apps-contract.md). Off until
  // a group has registered apps; backend per-user `features` wins.
  vendor_apps: false,
  // Warn when the chart open in the EMR is a different patient than the visit.
  patient_match_warning: false,
  // Thumbs up / down bar after each Comprehend click (saved via /v1/notes/feedback).
  comprehend_feedback: false,
  // Score All Notes (COM-390): bulk-grade notes clinicians wrote entirely on
  // their own, not just ones run through Comprehend's AI. Gates only the
  // bulk-trigger button + recent-runs panel on the analytics dashboard; the
  // note-source toggle and authorship badges ship unconditionally.
  score_all_notes: false,
  // COM-491: Visits page as the Sync Schedule day view (components/Visits).
  visits_schedule: false,
};

const ITOOLS_BASE_URL =
  'https://6g690gn5a2.execute-api.us-east-1.amazonaws.com/Prod';

class ApiClient {
  constructor() {
    this.backendTargets = BACKEND_TARGETS;
    this.isDev = null; // unknown until /v1/user/stats answers (see canUseCustomBackend)
    this.backendEnv = this.readBackendPreference();

    this.apiClient = axios.create({
      baseURL:
        this.backendTargets[this.backendEnv] ||
        this.backendTargets[DEFAULT_BACKEND_ENV],
      headers: {
        'Content-Type': 'application/json',
      },
    });
    this.sessionKey = getSessionKey();
    this.sessionEmail = getSessionEmail();

    this.name = null;
    this.minutesTranscribed = null;
    this.conversationsHad = null;
    this.patientsSeen = null;
    this.minutesSaved = null;
    this.configs = null;
    this.emr = null;
    this.active = null;
    this.creditsRemaining = null;
    this.email = null;
    this.is_beta = null;
    this.showAllEMRTemplates = null;
    this.groupSubscriptionId = null;
    this.isConfluentUser = false;
    this.conversations = [];
    this.patients = [];
    this.apiLog = []; // Rolling buffer of recent API calls (API_LOG_LIMIT) for bug reports + replay

    // Function calling Magic, this will be set by the assistant when it's created and called in every view
    this.ai_active = false;
    this.pendingTools = [];
    this.addTools = (tools) => {
      if (Array.isArray(tools)) {
        this.pendingTools.push(...tools);
      } else {
        this.pendingTools.push(tools);
      }
    };
    this.removeTools = () => false; // OpenAIAssistant will overwrite this method later!

    // Context/Memory Management - will be set by the assistant when it's created
    this.pendingContexts = new Map();
    this.addContext = (contextId, contextData) => {
      this.pendingContexts.set(contextId, contextData); // Replaces if already exists
    };
    this.removeContext = () => false; // OpenAIAssistant will overwrite this method later!

    this.features = {};
    this.newRaintreeIntegration = false;
    this.newPromptIntegration = false;
    this.newWebPTSoap2Integration = false;
    this.webpt2ChromeFreezeFix = false;
    this.userNoteRules = true;
    this._backendUpdateCallbacks = [];

    // Optional listeners that want to react when profile/features are loaded
    this._profileUpdateCallbacks = [];
    this.onProfileUpdated = (cb) => {
      if (typeof cb === 'function') {
        this._profileUpdateCallbacks.push(cb);
      }
    };
    this.offProfileUpdated = (cb) => {
      this._profileUpdateCallbacks = this._profileUpdateCallbacks.filter(
        (fn) => fn !== cb,
      );
    };

    // Apply any stored backend preference (dev-gated) now that the client exists
    this.applyBackendEnv(this.backendEnv);
  }

  // Backend environment helpers
  // The choice lives in localStorage so it survives closing the side panel
  // (sessionStorage died with it — flip to dev, close, reopen, back on prod).
  // sessionStorage is still read for anything that set it the old way.
  readBackendPreference() {
    try {
      const stored =
        localStorage.getItem(BACKEND_STORAGE_KEY) ||
        sessionStorage.getItem(BACKEND_STORAGE_KEY);
      if (stored && this.backendTargets[stored]) {
        return stored;
      }
    } catch (e) {
      console.warn('Unable to read backend env from storage', e);
    }
    return DEFAULT_BACKEND_ENV;
  }
  hasStoredBackendPreference() {
    try {
      const stored =
        localStorage.getItem(BACKEND_STORAGE_KEY) ||
        sessionStorage.getItem(BACKEND_STORAGE_KEY);
      return Boolean(stored && stored !== DEFAULT_BACKEND_ENV && this.backendTargets[stored]);
    } catch (e) {
      return false;
    }
  }
  clearBackendPreference() {
    try {
      localStorage.removeItem(BACKEND_STORAGE_KEY);
      sessionStorage.removeItem(BACKEND_STORAGE_KEY);
    } catch (e) {
      /* storage blocked */
    }
  }

  normalizeBackendEnv(env) {
    if (env && this.backendTargets[env]) {
      return env;
    }
    return DEFAULT_BACKEND_ENV;
  }

  canUseCustomBackend() {
    // Before stats have answered, a saved non-default choice is trusted — only
    // a dev user could have saved it, and it is what makes the FIRST request
    // (stats itself) go to the chosen backend. Stats then confirms or, for a
    // non-dev account, resets to prod and forgets the choice.
    if (this.isDev === null || this.isDev === undefined) {
      return this.hasStoredBackendPreference();
    }
    return Boolean(this.isDev);
  }

  applyBackendEnv(env) {
    const desired = this.normalizeBackendEnv(env);
    const effectiveEnv = this.canUseCustomBackend()
      ? desired
      : DEFAULT_BACKEND_ENV;
    this.backendEnv = effectiveEnv;
    this.apiClient.defaults.baseURL = this.backendTargets[effectiveEnv];

    try {
      if (this.canUseCustomBackend()) {
        localStorage.setItem(BACKEND_STORAGE_KEY, desired);
        sessionStorage.removeItem(BACKEND_STORAGE_KEY); // old location
      } else if (this.isDev === false) {
        this.clearBackendPreference(); // not a dev account: never hold a custom backend
      }
    } catch (e) {
      console.warn('Unable to persist backend env', e);
    }

    if (this._backendUpdateCallbacks.length) {
      this._backendUpdateCallbacks.forEach((cb) => {
        try {
          cb(effectiveEnv, this.backendTargets[effectiveEnv]);
        } catch (err) {
          console.error('Backend env callback failed', err);
        }
      });
    }
  }

  setBackendEnv(env) {
    this.applyBackendEnv(env);
  }

  getBackendEnv() {
    return this.backendEnv || DEFAULT_BACKEND_ENV;
  }

  getBackendTargets() {
    return { ...this.backendTargets };
  }

  onBackendUpdated = (cb) => {
    if (typeof cb === 'function') {
      this._backendUpdateCallbacks.push(cb);
    }
  };

  offBackendUpdated = (cb) => {
    this._backendUpdateCallbacks = this._backendUpdateCallbacks.filter(
      (fn) => fn !== cb,
    );
  };

  syncSidePanelMode(stats) {
    const sidePanelMode = stats?.side_panel_mode;
    const chromeApi = window.chrome;

    if (
      window.location.protocol !== 'chrome-extension:' ||
      !sidePanelMode ||
      !chromeApi?.storage?.sync
    ) {
      return;
    }

    chromeApi.storage.sync.set({ sidePanelMode });
  }

  setSessionKey(key) {
    storeSessionKey(key);
    this.sessionKey = key;
  }

  setSessionEmail(email) {
    storeSessionEmail(email);
    this.sessionEmail = email;
  }

  JMPost(url, data, alert = false) {
    // Check if we have a session key
    if (!this.haveSessionKey()) {
      // Go get it
      this.sessionKey = getSessionKey();
    }

    // Check if we have an email
    if (!this.sessionEmail) {
      // Go get it
      this.sessionEmail = getSessionEmail();
    }

    if (this.sessionKey === DEMO_ACCOUNT_SESSION_KEY) {
      // Check if the url is in the list of valid demo endpoints
      if (INVALID_DEMO_ENDPOINTS.includes(url)) {
        // return a reject promise
        const message = 'This feature is not available in Demo Mode';
        // console.log(url);
        window.alert(message);
        return new Promise((resolve, reject) => reject(message));
      }
    }
    // If we are out of credits in free mode then block specific requests
    if (!this.actionAllowed()) {
      if (SUBSCRIPTION_REQUIRED_ENDPOINTS.includes(url)) {
        // return a reject promise
        const message =
          'Your subscription has expired. Please renew your subscription to continue using ComprehendPT';
        // console.log(url);
        window.alert(message);
        return new Promise((resolve, reject) => resolve(message));
      }
    }

    // Add the session key to the request
    data['sessionKey'] = this.sessionKey;
    data['sessionEmail'] = this.sessionEmail;
    // COM-407 account-sharing detection
    data['client'] = window.ReactNativeWebView ? 'ios' : isCX() ? 'cx' : 'web';
    data['deviceId'] = getDeviceFingerprint();

    const logEntry = this.createApiLogEntry(url, data, true);

    // Wrap the API call in a try-catch block to handle errors
    return this.apiClient
      .post(url, data)
      .then((response) => {
        // Handle the successful response here

        // If the response code is 299 we have an invalid session key
        if (response.status === 299) {
          console.log('Invalid Session Key');

          // Push the user to the login page
          // COM-407: another device took over this account's session
          if (response.data?.code === 'SESSION_SUPERSEDED') {
            // iOS app: stop the native recorder and drop its key; reload stops web recording
            sendNativeMessage('stopRecording');
            sendNativeMessage('sessionKey', '');
            removeSessionKey(this);
            sessionStorage.setItem('signedOutElsewhere', 'true');
            window.location.reload();
          }
        }

        if ('error' in response.data) {
          // console.log(url + " => " + response.data['error'])
          if (alert) {
            alert(`Error: ${response.data['error']}`);
          }
        }

        this.recordApiLog(logEntry, response.status, response.data);

        return response.data;
      })
      .catch((error) => {
        // Handle different types of errors
        if (error.response) {
          // The request was made, but the server responded with an error
          console.error('Server Error');
          this.recordApiLog(
            logEntry,
            error.response.status,
            error.response.data,
            'Server Error',
          );
          // The message stays 'Server Error' (callers match on it); the
          // server's own explanation rides along for callers that show it.
          const serverError = new Error('Server Error');
          serverError.serverMessage = error.response.data?.error;
          serverError.status = error.response.status;
          throw serverError;
        } else if (error.request) {
          // The request was made, but no response was received
          console.error('No response received');
          this.recordApiLog(logEntry, null, null, 'No response received');
          throw new Error('No response received');
        } else {
          // Something else went wrong
          console.error('Request setup failed');
          this.recordApiLog(logEntry, null, null, 'Request setup failed');
          throw new Error('Unknown Error');
        }
      });
  }

  /**
   * Append to the rolling API log and mirror the call onto any running session
   * replay.
   *
   * One place rather than four: the buffer bound and the replay hand-off used to
   * be duplicated at every call site, which is how they drift.
   *
   * The replay hand-off is deliberately fire-and-forget and swallowed — a
   * diagnostic stream must never turn a working API call into a failed one.
   * Bodies never enter this buffer. Authenticated JMPost bodies are passed to
   * the replay sidecar separately; JMPostPublic remains metadata-only.
  */
  createApiLogEntry(url, request, captureReplayBodies = false) {
    const replayIdAtStart = getActiveReplayId();
    const path = String(url || '').split(/[?#]/, 1)[0];
    return {
      timestamp: new Date().toISOString(),
      startedAt: Date.now(),
      url: path,
      requestBytes: apiLogBodyBytes(request),
      replayIdAtStart,
      // Checked here rather than at emit time: this is where the body is
      // cloned, and cloning an audio upload is the expensive half.
      replayRequest:
        captureReplayBodies && replayIdAtStart && !bodylessInReplay(path)
          ? replayBodySnapshot(request)
          : undefined,
    };
  }

  recordApiLog(logEntry, status = null, response = null, error = null) {
    const entry = {
      timestamp: logEntry.timestamp,
      url: logEntry.url,
      method: 'POST',
      status,
      error,
      durationMs: Math.max(0, Date.now() - logEntry.startedAt),
      requestBytes: logEntry.requestBytes,
      responseBytes: apiLogBodyBytes(response),
    };
    this.apiLog.push(entry);
    if (this.apiLog.length > API_LOG_LIMIT) this.apiLog.shift();
    // See REPLAY_EXCLUDED_PREFIXES: the replay's own traffic and credentials
    // never reach the timeline. Audio uploads do — without their bodies.
    if (excludedFromReplay(entry.url)) return;
    // A request that straddles logout or impersonation still belongs to the
    // replay active when it began, never the next user's replay.
    if (logEntry.replayIdAtStart !== getActiveReplayId()) return;
    try {
      noteApiCall(
        logEntry.replayRequest === undefined
          ? entry
          : { ...entry, request: logEntry.replayRequest, response },
      );
    } catch (e) {
      // Never surface — see above.
    }
  }

  getApiLog() {
    return this.apiLog;
  }

  // Public API method that doesn't add session authentication
  JMPostPublic(url, data, alert = false) {
    const logEntry = this.createApiLogEntry(url, data);

    // Wrap the API call in a try-catch block to handle errors
    return this.apiClient
      .post(url, data)
      .then((response) => {
        // Handle the successful response here
        if ('error' in response.data) {
          // console.log(url + " => " + response.data['error'])
          if (alert) {
            alert(`Error: ${response.data['error']}`);
          }
        }
        this.recordApiLog(logEntry, response.status, response.data);
        return response.data;
      })
      .catch((error) => {
        // Handle different types of errors
        if (error.response) {
          // The request was made, but the server responded with an error
          console.error('Server Error');
          this.recordApiLog(
            logEntry,
            error.response.status,
            error.response.data,
            'Server Error',
          );
          // You can return an error response or throw an error if needed
          throw new Error('Server Error');
        } else if (error.request) {
          // The request was made, but no response was received
          console.error('No response received');
          this.recordApiLog(logEntry, null, null, 'No response received');
          // You can return an error response or throw an error if needed
          throw new Error('No response received');
        } else {
          // Something else went wrong
          console.error('Request setup failed');
          this.recordApiLog(logEntry, null, null, 'Request setup failed');
          // You can return an error response or throw an error if needed
          throw new Error('Something went wrong');
        }
      });
  }

  // Auth
  // JMPost reads the key synchronously, so the request carries it even though
  // the caller clears storage and reset()s right after this returns.
  logout() {
    if (!this.haveSessionKey() && !getSessionKey()) return;
    this.JMPost('/v1/auth/logout', {}).catch(() => {});
  }

  login(
    username,
    password,
    remember,
    ip_address,
    os_Type,
    os_version,
    browser_name,
    user_agent,
    app_version,
    mfaCode = '',
    takeover = false,
  ) {
    const body = {
      username: username,
      password: password,
      ip_address: ip_address,
      os_Type: os_Type,
      os_version: os_version,
      browser_name: browser_name,
      user_agent: user_agent,
      app_version: app_version,
    };
    if (mfaCode && mfaCode.length > 0) {
      body['mfa_code'] = mfaCode;
    }
    if (takeover) {
      body['takeover'] = true;
    }
    return this.JMPost('/v1/auth/login', body)
      .then((data) => {
        // An MFA challenge or an unverified-email rejection comes back without
        // a sessionKey; don't write `undefined` into session storage.
        if (!data || !data['sessionKey']) {
          return data;
        }
        // If we got a session key then store it locally
        this.setSessionKey(data['sessionKey']);
        this.setSessionEmail(body['username']);

        // Store the session key in the browser if we should remember it
        // console.log("Remember:")
        // console.log(remember)
        if (remember) {
          console.log('Storing Local Key');
          storeLocalData(this.sessionKey);
          storeLocalData(this.sessionEmail, true);
        }

        return data;
      })
      .catch((e) => {
        return {
          error: 'Please disconnect from the VPN (if connected) and try again.',
        };
      });
  }

  // Where to send the browser to start "Continue with Google". This is a full
  // page navigation, not an XHR - the backend answers with a 302 to Google and
  // eventually redirects back to /User/Home?sessionKey=..&sessionEmail=..
  // Reads the live axios baseURL so the dev/local backend switcher works.
  googleSignInUrl() {
    const base =
      this.apiClient?.defaults?.baseURL ||
      this.backendTargets[DEFAULT_BACKEND_ENV];
    return `${base}/v1/auth/google/start`;
  }

  // Same as googleSignInUrl, for "Continue with Microsoft".
  microsoftSignInUrl() {
    const base =
      this.apiClient?.defaults?.baseURL ||
      this.backendTargets[DEFAULT_BACKEND_ENV];
    return `${base}/v1/auth/microsoft/start`;
  }

  register(
    name,
    email,
    password,
    profession,
    speciality,
    emr,
    referralCode,
    remember,
    ip_address,
    os_Type,
    os_version,
    browser_name,
    user_agent,
    app_version,
    googleSignupToken,
    demoAnonymousId,
  ) {
    const body = {
      name: name,
      email: email,
      password: password,
      profession: profession,
      speciality: speciality,
      EMR: emr,
      referralCode: referralCode,
      ip_address: ip_address,
      os_Type: os_Type,
      os_version: os_version,
      browser_name: browser_name,
      user_agent: user_agent,
      app_version: app_version,
      // Optional: only present when this browser has a self-serve demo trail.
      // Omitted entirely otherwise so /v1/auth/new sees a missing key, not null.
      ...(demoAnonymousId ? { demoAnonymousId } : {}),
    };
    // Only present when the user arrived from the Google callback. It stands in
    // for the password and proves Google already vouched for the address.
    if (googleSignupToken) {
      body['googleSignupToken'] = googleSignupToken;
    }
    console.log(body);

    return this.JMPost('/v1/auth/new', body)
      .then((data) => {
        // When email verification is required the backend creates the account
        // but withholds the sessionKey. Storing `undefined` here would leave a
        // junk key in session storage, so bail out and let the caller route to
        // the "check your email" screen instead.
        if (!data || !data['sessionKey']) {
          return data;
        }
        // If we got a session key then store it locally
        this.setSessionKey(data['sessionKey']);
        this.setSessionEmail(body['email']);
        // Store the session key in the browser if we should remember it
        if (remember) {
          storeLocalData(this.sessionKey);
          storeLocalData(this.sessionEmail, true);
        }
        return data;
      })
      .catch((e) => {
        return {
          error: 'Please disconnect from the VPN (if connected) and try again.',
        };
      });
  }

  reset() {
    // Prevent Appcues UI and identity from carrying across logout/session swaps.
    if (window.Appcues) {
      window.Appcues.clearShow?.();
      window.Appcues.reset();
    }
    // Same reasoning for session replay: stop recording now, so nothing the
    // NEXT user does on this tab is appended to the previous user's replay, and
    // clear the one-shot latch so they get their own policy decision. Static
    // import — ./replay only reaches rrweb through a dynamic import, so this
    // does not pull the recorder library into the main bundle.
    discardSessionReplay();
    this.sessionKey = null;
    this.sessionEmail = null;
    this.name = null;
    this.minutesTranscribed = null;
    this.conversationsHad = null;
    this.patientsSeen = null;
    this.minutesSaved = null;
    this.configs = null;
    this.active = null;
    this.creditsRemaining = null;
    this.email = null;
    this.is_beta = null;
    this.showAllEMRTemplates = null;
    this.conversations = [];
    this.patients = [];
  }

  // Checkout Session Grabbing when referred from Stripe
  getCheckoutSession = (cs) =>
    this.JMPost('/v1/checkout/getSessionDetails', { checkoutSession: cs });

  // User
  // Runs as a background job and polls for it: a large clinic over a long
  // range can take longer than API Gateway's 29 s. Resolves with the same body
  // /v1/user/analytics returns; a backend without jobs answers directly.
  getUserAnalytics = async (
    t1,
    t2,
    page,
    pageSize,
    labels = [],
    userEmails = [],
    ruleSet = null,
    source,
  ) => {
    const started = await this.JMPost('/v1/user/analytics', {
      t1: t1,
      t2: t2,
      page: page,
      page_size: pageSize,
      labels: labels,
      user_emails: userEmails,
      rule_set: ruleSet,
      source: source,
      background: true,
    });
    if (!started?.jobId) return started;
    const deadline = Date.now() + 17 * 60 * 1000;
    let failures = 0;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      let job;
      try {
        job = await this.JMPost('/v1/user/analytics/status', { jobId: started.jobId });
        failures = 0;
      } catch (error) {
        if (++failures >= 3) throw error;
        continue;
      }
      if (job?.status === 'completed') return job.result;
      if (job?.error || job?.status === 'failed') {
        return { error: job.error || 'Unable to load analytics. Try again.' };
      }
    }
    return { error: 'Loading analytics took too long. Try again.' };
  };
  getUserVisits = (userEmail, t1, t2, ruleSet = null, source) =>
    this.JMPost('/v1/user/analytics/visits', {
      userEmail: userEmail,
      t1: t1,
      t2: t2,
      rule_set: ruleSet,
      source: source,
    });
  getVisitTypeBreakdown = (userEmail, t1, t2, source, ruleSet) =>
    this.JMPost('/v1/user/analytics/visit/details', {
      userEmail: userEmail,
      t1: t1,
      t2: t2,
      source: source,
      rule_set: ruleSet,
    });

  // Score All Notes (COM-390): bulk-grade notes the clinician wrote entirely
  // on their own (no Comprehend AI involved), across a date range / clinician
  // filter set. Small batches (<=25 notes) are graded synchronously and the
  // backend returns a result directly instead of a jobId; larger batches
  // return {jobId, status: 'queued'} and the caller polls getScoreAllStatus.
  // exportFile: always run the job (even for <=25 notes) so the runner
  // writes the raw compliance CSV before reporting completion.
  startScoreAllNotes = (t1, t2, labels, userEmails, exportFile, ruleSet, source) =>
    this.JMPost('/v1/notes/score-all/start', {
      t1,
      t2,
      labels,
      user_emails: userEmails,
      export: exportFile,
      rule_set: ruleSet,
      source,
    });
  // COM-564: build the stored report for a finished run that has none (runs
  // from before reports existed). Report only -- no regrading, same CSV.
  buildScoreAllReport = (jobId) =>
    this.JMPost('/v1/notes/score-all/report/build', { jobId });
  // COM-564: stop a queued/running run. Grades already written stay.
  cancelScoreAllRun = (jobId) =>
    this.JMPost('/v1/notes/score-all/cancel', { jobId });
  // A run's stored analytics report, read back in parts (a clinic-wide quarter
  // is larger than one API response). Resolves to the parsed
  // {builtAt, filters, report}, or to {status, error} when there is none yet.
  getScoreAllReport = async (jobId, attempt = 0) => {
    let text = '';
    let builtAt;
    for (let part = 0, parts = 1; part < parts; part += 1) {
      // eslint-disable-next-line no-await-in-loop
      const response = await this.JMPost('/v1/notes/score-all/report', { jobId, part });
      if (response?.error && response?.status !== 'unavailable') {
        throw new Error(response.error);
      }
      if (response?.status !== 'ready') return response;
      if (part === 0) {
        builtAt = response.builtAt;
      } else if (response.builtAt !== builtAt) {
        // A rerun replaced the file part way through: start over, once.
        if (attempt > 0) throw new Error('The report changed while loading. Try again.');
        return this.getScoreAllReport(jobId, attempt + 1);
      }
      text += response.data;
      parts = response.parts;
    }
    return { status: 'ready', ...JSON.parse(text) };
  };
  getScoreAllStatus = (jobId) =>
    this.JMPost('/v1/notes/score-all/status', { jobId });
  // {status: 'ready', url} once the job's CSV exists; otherwise its status.
  getScoreAllExport = (jobId) =>
    this.JMPost('/v1/notes/score-all/export', { jobId });
  // Server-backed list of past score-all runs, so a run can be resurfaced by
  // id (e.g. from the recent-runs panel or an email deep link) without
  // re-running it. Scope note: this only resurfaces exact prior jobs by id --
  // no date-range-overlap detection or partial-result merging (backend-
  // deferred, out of scope for COM-390 frontend work).
  listScoreAllRuns = () => this.JMPost('/v1/notes/score-all/list', {});
  // Learn / Learn++ analytics. One endpoint, several views (overview,
  // sections, by, outcomes, drift, changes, filters) so the dashboard can
  // switch tabs without a route each. Admin/coordinator only.
  getLearnEdits = (view, t1, t2, options = {}) =>
    this.JMPost('/v1/analytics/learn-edits', {
      view,
      t1,
      t2,
      ...options,
    });

  // options: config_name (the card's note type), rule_set, source.
  aggregateFeedback = (userEmail, t1, t2, options = {}) =>
    this.JMPost('/v1/user/analytics/aggregateFeedback', {
      userEmail: userEmail,
      t1: t1,
      t2: t2,
      ...options,
    });
  getAgreements = (agreement) =>
    this.JMPost('/v1/user/getAgreements', { agreement: agreement });
  getExpiration = async () => {
    try {
      const cached = await cacheManager.get('user_expiration');
      if (cached) return cached;

      const response = await this.JMPost('/v1/user/expiration', {});
      if (response && !response.error) {
        await cacheManager.set('user_expiration', response, 2 * 60 * 1000); // 2 min cache
      }
      return response;
    } catch (error) {
      console.error('Cache error:', error);
      return this.JMPost('/v1/user/expiration', {});
    }
  };
  getUserConversations = (group, startDate, endDate) => {
    if (startDate && endDate) {
      return this.JMPost('/v1/user/conversations', {
        group: group,
        startDate: startDate,
        endDate: endDate,
      });
    } else {
      return this.JMPost('/v1/user/conversations', { group: group });
    }
  };
  getPatientHistory = (group, look_back_months = 3) =>
    this.JMPost('/v1/patient/history', {
      group: group,
      look_back_months: look_back_months,
    });
  getUserGroups = async () => {
    try {
      const cached = await cacheManager.get('user_groups');
      if (cached) return cached;

      const response = await this.JMPost('/v1/user/groups', {});
      if (response && !response.error) {
        await cacheManager.set('user_groups', response, 5 * 60 * 1000); // 5 min cache
      }
      return response;
    } catch (error) {
      console.error('Cache error:', error);
      return this.JMPost('/v1/user/groups', {});
    }
  };
  getUserConfigs = async () => {
    try {
      const cached = await cacheManager.get('user_configs');
      if (cached) return cached;

      const response = await this.JMPost('/v1/user/configs', {});
      if (response && !response.error) {
        await cacheManager.set('user_configs', response, 5 * 60 * 1000); // 5 min cache
      }
      return response;
    } catch (error) {
      console.error('Cache error:', error);
      return this.JMPost('/v1/user/configs', {});
    }
  };
  applyPromo = (promoData) => this.JMPost('/v1/user/promo', promoData);
  getStats = async () => {
    try {
      const cached = await cacheManager.get('user_stats');
      if (cached) {
        this.syncSidePanelMode(cached);
        return cached;
      }

      const response = await this.JMPost('/v1/user/stats', {});
      if (response && !response.error) {
        this.syncSidePanelMode(response);
        await cacheManager.set('user_stats', response, 5 * 60 * 1000); // 5 min cache
      }
      return response;
    } catch (error) {
      console.error('Cache error:', error);
      const response = await this.JMPost('/v1/user/stats', {});
      this.syncSidePanelMode(response);
      return response;
    }
  };
  // Record an onboarding step on the account (merged server-side) and notify
  // the checklist. No-op if already set.
  markOnboarding = (step, value = true) => {
    this.onboarding = this.onboarding || {};
    if (this.onboarding[step] === value) return Promise.resolve();
    this.onboarding = { ...this.onboarding, [step]: value };
    // Same-window only: OnboardingChecklist (the pill in this app) listens for
    // this to re-render and fire the first-real-note cheer right away instead
    // of waiting for the next profile refresh. Nothing is sent to the
    // Playground or any other origin.
    try { window.dispatchEvent(new CustomEvent('comprehend:onboarding', { detail: { step, value } })); } catch (e) { /* ok */ }
    return this.editUserSettings({ onboarding: { [step]: value } }).catch(() => {});
  };

  // COM-561: record a choice from the old/new integration switch. The flag it
  // sets applies right away (applyFeatures), and the next scan sends it with
  // GET_DOM.
  saveIntegrationSwitch = async (action, { reason, comment } = {}) => {
    const emr = this.integrationSwitch?.emr;
    if (!emr) return { error: 'No integration switch is open' };
    cacheManager.remove('user_stats');
    const response = await this.JMPost('/v1/user/integrationSwitch', {
      emr,
      action,
      reason,
      comment,
    });
    cacheManager.remove('user_stats');
    if (!response || response.error) return response || { error: 'No response' };
    this.integrationSwitch = response.integrationSwitch || null;
    const features = response.features || {};
    // Before the listeners run, so anything they start already sees it.
    const upgrade = INTEGRATION_UPGRADES.find((row) => row.emr === emr);
    if (upgrade && upgrade.feature in features) {
      this[upgrade.property] = features[upgrade.feature];
    }
    this.applyFeatures(features);
    return response;
  };

  // Applies flag values the backend has already saved, without waiting for the
  // next /v1/user/stats: merges them over the loaded flags (which include
  // defaults the stored features lack) and re-fires the profile listeners,
  // which push the flags to the content script. Saves nothing, and leaves the
  // shortcut properties updateProfile sets (newPromptIntegration, ...) to the
  // caller.
  applyFeatures = (changes) => {
    this.features = { ...this.features, ...changes };
    this._profileUpdateCallbacks.forEach((cb) => {
      try {
        cb(this);
      } catch (e) {
        console.error('Profile update callback failed', e);
      }
    });
  };

  editUserSettings = (userSettingsData) => {
    cacheManager.remove('user_stats');
    // Again once it lands: a stats read that was out meanwhile cached the old value
    return this.JMPost('/v1/user/settings', userSettingsData).finally(() => cacheManager.remove('user_stats'));
  };
  setDefaultConfigId = (configId) => {
    // Invalidate caches that depend on default config
    cacheManager.remove('user_stats');
    cacheManager.remove('user_configs');
    return this.JMPost('/v1/user/setDefaultConfigId', { configId: configId });
  };
  getUserSharedConfigs = async (userGroup) => {
    try {
      const cacheKey = `shared_configs_${userGroup}`;
      const cached = await cacheManager.get(cacheKey);
      if (cached) return cached;

      const response = await this.JMPost('/v1/user/sharedConfigs', {
        userGroup,
      });
      if (response && !response.error) {
        await cacheManager.set(cacheKey, response, 10 * 60 * 1000); // 10 min cache
      }
      return response;
    } catch (error) {
      console.error('Cache error:', error);
      return this.JMPost('/v1/user/sharedConfigs', { userGroup });
    }
  };
  async getAllConfigs() {
    // verify we have their stats and if not get them.
    console.log('Getting all configs');
    if (!this.email) {
      await this.updateProfile();
    }
    console.log(this.email);

    const userConfigsPromise = this.getUserConfigs();
    const userSharedConfigsPromise = this.getUserSharedConfigs(
      '@' + this.email.split('@')[1],
    );

    return Promise.all([userConfigsPromise, userSharedConfigsPromise])
      .then(([userConfigs, userSharedConfigs]) => {
        // console.log('User Configs:', userConfigs);
        // console.log('User Shared Configs:', userSharedConfigs);
        // the shared configs have a weird thing where name is marked as sharedConfigName and id is sharedConfigId
        // Please also map the sharedConfigs to the same format as the userConfigs
        userSharedConfigs['sharedConfigs'] = userSharedConfigs[
          'sharedConfigs'
        ].map((config) => {
          // console.log('Config', config);
          return {
            // the normal config + the name and configId
            ...config,
            name: config.sharedConfigName + ` (${config.sharedConfigOwner})`,
            configId: config.sharedConfigId,
          };
        });
        this.configs = [
          ...userConfigs['configs'],
          ...userSharedConfigs['sharedConfigs'],
        ];
        return this.configs;
      })
      .catch((error) => {
        console.error('Error fetching configs:', error);
        throw error;
      });
  }
  updateMultiFactorAuth = () => this.JMPost('/v1/user/mfa', {});
  verifyMultiFactorAuth = (email, mfaCode) =>
    this.JMPost('/v1/auth/verifyMfa', {
      email: email,
      mfa_code: mfaCode,
    });

  //Group Subscription
  getGroupSubscriptionStats = (group) =>
    this.JMPost('/v1/subscription/group/stats', {});
  getNewSubEstimate = (seats) =>
    this.JMPost('/v1/subscription/group/newSeatsEstimate', { seats: seats });
  addMoreSeats = (emails, seats) =>
    this.JMPost('/v1/subscription/group/addSeats', {
      emails: emails,
      seats: seats,
    });
  inviteUsers = (emails, role = 'User') =>
    this.JMPost('/v1/subscription/group/inviteUsers', { emails, role });
  changeUserRole = (email, role) =>
    this.JMPost('/v1/subscription/group/changeRole', {
      email: email,
      role: role,
    });
  removeUser = (email) =>
    this.JMPost('/v1/subscription/group/removeUser', { email: email });
  sendUserEmailReminder = (email) =>
    this.JMPost('/v1/subscription/group/sendEmailReminder', { email: email });

  // Account & Billing (COM-471)
  getBillingOverview = () => this.JMPost('/v1/billing/overview', {});
  addBillingSeats = (seats) => this.JMPost('/v1/billing/seats/add', { seats });
  removeBillingSeats = (seats) =>
    this.JMPost('/v1/billing/seats/remove', { seats });
  startPaymentMethodUpdate = () =>
    this.JMPost('/v1/billing/paymentMethod/session', {});
  confirmPaymentMethodUpdate = (checkoutSession) =>
    this.JMPost('/v1/billing/paymentMethod/confirm', { checkoutSession });
  setInvoiceEmails = (emails) =>
    this.JMPost('/v1/billing/invoiceEmails', { emails });
  changeAccountOwner = (email) => this.JMPost('/v1/billing/owner', { email });
  cancelSubscription = (reasons, details, note) =>
    this.JMPost('/v1/billing/cancel', { reasons, details, note });
  resumeSubscription = () => this.JMPost('/v1/billing/resume', {});
  startCheckout = (seats, interval, embedded = false) =>
    this.JMPost('/v1/billing/checkout', { seats, interval, embedded });
  requestIntegration = (request) =>
    this.JMPost('/v1/support/integrationRequest', request);
  getRawUsageData = (t1, t2, users, getGroupUsers = false) =>
    this.JMPost('/v1/subscription/group/rawUsageData', {
      t1: t1,
      t2: t2,
      users: users,
      getGroupUsers: getGroupUsers,
    });
  startAdoptionReport = (request) =>
    this.JMPost('/v1/subscription/group/adoptionReport/start', { ...request });
  listAdoptionReports = () =>
    this.JMPost('/v1/subscription/group/adoptionReport/list', {});
  getAdoptionReportDownload = (jobId) =>
    this.JMPost('/v1/subscription/group/adoptionReport/download', { jobId });

  //Password Reset
  sendResetPasswordEmail(email) {
    const body = {
      username: email,
    };

    return this.JMPost('/v1/reset/email', body)
      .then((data) => {
        return data;
      })
      .catch((e) => {
        return {
          error: 'Please disconnect from the VPN (if connected) and try again.',
        };
      });
  }
  resetPassword = (password, token) =>
    this.JMPost('/v1/reset/password', { password: password, token: token });

  //Email Verification
  // Sends (or re-sends) the signup verification email. Mirrors the shape of
  // sendResetPasswordEmail so the backend can reuse the same mailer plumbing.
  sendVerificationEmail(email) {
    const body = {
      username: email,
    };

    return this.JMPost('/v1/verify/email', body)
      .then((data) => {
        return data;
      })
      .catch((e) => {
        return {
          error: 'Please disconnect from the VPN (if connected) and try again.',
        };
      });
  }

  // Redeems the token from the verification link. On success the account is
  // marked verified and the user can log in normally.
  confirmEmailVerification(token) {
    return this.JMPost('/v1/verify/confirm', { token: token })
      .then((data) => {
        return data;
      })
      .catch((e) => {
        return {
          error: 'Please disconnect from the VPN (if connected) and try again.',
        };
      });
  }

  // "Verify by text instead" - the alternate path off the check-your-email
  // screen for someone who can't get to their inbox. Same account, same
  // email_verified flag; this just gives it a second way to flip to true.
  sendVerificationSms(email, phone) {
    return this.JMPost('/v1/verify/sms', { username: email, phone: phone })
      .then((data) => {
        return data;
      })
      .catch((e) => {
        return {
          error: 'Please disconnect from the VPN (if connected) and try again.',
        };
      });
  }

  // Checks the 6-digit code the user typed back in. Like
  // confirmEmailVerification, success only marks the account verified - the
  // user then signs in with their password.
  confirmSmsVerification(email, code) {
    return this.JMPost('/v1/verify/sms/confirm', {
      username: email,
      code: code,
    })
      .then((data) => {
        return data;
      })
      .catch((e) => {
        return {
          error: 'Please disconnect from the VPN (if connected) and try again.',
        };
      });
  }

  // Patient
  newPatient = (first, last, dob) => {
    cacheManager.remove('user_groups');
    return this.JMPost('/v1/patient/new', {
      firstName: first,
      lastName: last,
      dob: dob,
    });
  };
  patientEMRQuery = (first, last, dob, emr, metadata) => {
    cacheManager.remove('user_groups');
    return this.JMPost('/v1/patient/emrQuery', {
      firstName: first,
      lastName: last,
      dob: dob,
      emrID: emr,
      metadata: metadata,
    });
  };
  // Vendor Apps: persist (or clear with null) a vendor's own id for one of our
  // patients; comes back on /v1/user/groups as `vendorRefs`.
  setPatientVendorRef = (patientId, appId, patientRef) =>
    this.JMPost('/v1/patient/vendorRef', { patientId, appId, patientRef });
  // Admin → Apps registration (group admins/coordinators only)
  listVendorApps = () => this.JMPost('/v1/vendorApps/list', {});
  upsertVendorApp = (app) => this.JMPost('/v1/vendorApps/upsert', { app });
  deleteVendorApp = (appId) => this.JMPost('/v1/vendorApps/delete', { appId });
  // What a connected app says about the clinician (comprehend.setProviderContext):
  // kept on the user record and read by chat, never by note prompts.
  getProviderContext = () => this.JMPost('/v1/vendorApps/providerContext/get', {});
  // Called only by the authenticated host; the iframe never receives this client.
  getVendorAppData = (appId, appOrigin) => this.JMPost('/v1/vendorApps/data/get', { appId, appOrigin });
  saveVendorAppData = (appId, appOrigin, value, expectedRevision) => this.JMPost('/v1/vendorApps/data/save', { appId, appOrigin, value, expectedRevision });
  setProviderContext = (appId, markdown) => this.JMPost('/v1/vendorApps/providerContext/set', { appId, markdown });
  // Server-side frame-ancestors / X-Frame-Options check for a vendor URL (public endpoint)
  checkVendorFraming = (url) => this.JMPost('/v1/vendorApps/checkFraming', { url });
  // Paste-a-link registration: origin + vendor manifest (/.well-known/comprehend-app.json) + framing, in one call.
  resolveVendorApp = (url, manifestUrl = null) => this.JMPost('/v1/vendorApps/resolve', manifestUrl ? { url, manifestUrl } : { url });

  editPatient = (patientId, key, val) => {
    cacheManager.remove('user_groups');
    return this.JMPost('/v1/patient/edit', {
      patientId: patientId,
      fieldName: key,
      value: val,
    });
  };
  deletePatient = (patientId) => {
    cacheManager.remove('user_groups');
    return this.JMPost('/v1/patient/delete', { patientId: patientId });
  };

  // Config
  // newConfig = (configName) => this.JMPost('/v1/config/new', {}); Not ready yet
  newConfig = () => new Promise((resolve, reject) => reject('Not Ready Yet'));
  duplicateConfig = (existingConfig, newConfig) => {
    cacheManager.remove('user_configs');
    return this.JMPost('/v1/config/new', {
      existingConfig: existingConfig,
      newConfig: newConfig,
    });
  };

  newTemplate = () => {
    cacheManager.remove('user_configs');
    return this.JMPost('/v1/template/new', {});
  };

  templateEMRQuery = (
    emrURL,
    noteType,
    sections,
    sectionMetadata,
    replyTypesOnly,
  ) => {
    cacheManager.remove('user_configs');
    return this.JMPost('/v1/template/emrQuery', {
      emrURL: emrURL,
      noteType: noteType,
      sections: sections || '',
      sectionMetadata: sectionMetadata,
      replyTypesOnly: replyTypesOnly,
    });
  };
  getConfigDetails = (configId) =>
    this.JMPost('/v1/config/details', { configId: configId });
  editConfig = (configId, fieldName, key, value, keyNew) => {
    cacheManager.remove('user_configs');
    return this.JMPost('/v1/config/edit', {
      configId: configId,
      fieldName: fieldName,
      key: key,
      value: value,
      keyNew: keyNew,
    });
  };
  editConfigBulk = (configId, bulk) =>
    this.JMPost('/v1/config/editBulk', { configId: configId, bulkEdits: bulk });
  reorderConfig = (configId, fieldName, newIndex) => {
    cacheManager.remove('user_configs');
    return this.JMPost('/v1/config/reorder', {
      configId: configId,
      fieldName: fieldName,
      newIndex: newIndex,
    });
  };
  deleteConfig = (configId) => {
    cacheManager.remove('user_configs');
    return this.JMPost('/v1/config/delete', { configId: configId });
  };
  duplicateTemplate = (existingConfig, newConfig) => {
    cacheManager.remove('user_configs');
    return this.JMPost('/v1/config/duplicate', {
      existingConfigID: existingConfig,
      newConfigName: newConfig,
    });
  };
  shareTemplate = (configId, recipientAccessMapping) =>
    this.JMPost('/v1/config/shareTemplate', {
      configId: configId,
      recipientAccessMapping: recipientAccessMapping,
    });
  shareTemplateDetails = (configId) =>
    this.JMPost('/v1/config/shareTemplate/details', { configId: configId });

  genInstruction = (configId, questionKey, primer) =>
    this.JMPost('/v1/config/genInstruction', {
      configId: configId,
      questionKey: questionKey,
      primer: primer,
    });

  genInstructionsInline = (
    configId,
    sectionsToGenerate,
    allKnownSections,
    visibleSections,
  ) =>
    this.JMPost('/v1/config/genInstructionsInline', {
      configId: configId,
      sectionsToGenerate: sectionsToGenerate,
      allKnownSections: allKnownSections,
      visibleSections: visibleSections,
    });

  planInstructionChanges = (configId, feedback, sectionMetadata) =>
    this.JMPost('/v1/config/planInstructionChanges', {
      configId: configId,
      feedback: feedback,
      sectionMetadata: sectionMetadata || null,
    });

  generateInstructionRevisions = (
    configId,
    changes,
    sectionMetadata,
    feedbackContext = {},
    instructionChangeEvent = null,
  ) => {
    const feedback = feedbackContext || {};
    const data = {
      configId: configId,
      changes: changes,
      sectionMetadata: sectionMetadata || null,
      feedbackTranscript: feedback.transcript || '',
      feedbackSource: feedback.source || 'typed',
      visitTranscript: feedback.visitTranscript || '',
    };
    if (instructionChangeEvent) {
      data.instructionChangeEvent = instructionChangeEvent;
    }
    return this.JMPost('/v1/config/generateInstructionRevisions', data);
  };

  getBuilderConfig = (builderId, emrURL, noteType) =>
    this.JMPost('/v1/config/getBuilderTemplate', {
      configId: builderId || null,
      emrURL: emrURL || null,
      noteType: noteType || null,
    });

  genTemplate = (configId, section, useSamples) =>
    this.JMPost('/v1/template/genTemplate', {
      configId: configId,
      section: section || null,
      useSamples: useSamples || false,
    });

  getUploadUrlsForPDF(configId, files) {
    const fileNames = files.map((file) => file.name); // Extract file names
    const uploadedDocuments = []; // Array to collect the document details
    // Make a single request to get all URLs for the files
    return this.JMPost('/v1/config/getUrls', {
      configId: parseInt(configId),
      fileNames: fileNames, // Send all file names at once
    })
      .then((data) => {
        if ('error' in data) {
          console.log('Failed to get URLs');
          return Promise.reject('Failed to get URLs');
        }

        const urls = data; // Get all URLs

        // Check if the number of URLs matches the number of files
        if (Object.keys(urls).length !== files.length) {
          return Promise.reject('Mismatch between number of files and URLs');
        }

        // Upload each file to its corresponding presigned URL
        const uploadPromises = files.map((file) => {
          const url = urls[file.name]; // Get the corresponding URL for the file by its name
          console.log(`Uploading ${file.name} to URL: ${url}`);

          // Upload the PDF file to the presigned URL
          return fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/pdf' },
            body: file, // This is the actual file content (Blob or File object)
          }).then((response) => {
            if (!response.ok) {
              console.error(
                `Failed to PUT file ${file.name}:`,
                response.status,
                response.statusText,
              );
              return Promise.reject(`Failed to PUT file ${file.name}`);
            }

            console.log(`Successfully uploaded ${file.name}`);

            // Collect the uploaded document details
            uploadedDocuments.push(url);

            return Promise.resolve(); // Successfully uploaded this file
          });
        });

        // Wait for all files to be uploaded and then call addDocuments
        return Promise.all(uploadPromises)
          .then(() => {
            console.log('All files uploaded successfully. Adding documents...');
            // Call addDocuments with all the uploaded document details
            return this.JMPost('/v1/config/addDocuments', {
              urls: uploadedDocuments,
              configId: parseInt(configId),
            })
              .then((response) => {
                console.log('Documents successfully added:', response);
              })
              .catch((error) => {
                console.error('Error while adding documents:', error);
              });
          })
          .catch((error) => {
            console.error('Error during file upload process:', error);
          });
      })
      .catch((error) => {
        console.error('Error during URL fetching process:', error);
      });
  }

  getPDFBuilderDocs = () => this.JMPost('/v1/config/getPDFBuilderDocs', {});

  addNoteType = (noteType, emrUrl, configId) =>
    this.JMPost('/v1/config/addNoteType', {
      noteType: noteType,
      emrUrl: emrUrl || '',
      configId: configId || null,
    });

  downloadPDFBuilderDoc = (configId, documentName) =>
    this.JMPost('/v1/config/downloadPDFBuilderDoc', {
      configId: parseInt(configId),
      documentName: documentName,
    });

  deleteDocument = (configId, documentName) =>
    this.JMPost('/v1/config/deleteDocument', {
      configId: parseInt(configId),
      documentName: documentName,
    });

  getSections = (configId) =>
    this.JMPost('/v1/config/getSections', {
      configId: parseInt(configId),
    });

  getSectionsText = (configId, sections) =>
    this.JMPost('/v1/config/getSectionsText', {
      configId: parseInt(configId),
      sections: sections,
    });

  getSectionsInstructions = (configId, sections) =>
    this.JMPost('/v1/config/getSectionsInstructions', {
      configId: parseInt(configId),
      updated_training_data: sections,
    });

  autoGenWithPDFs = (configId, sections) =>
    this.JMPost('/v1/config/autoGenWithPDFs', {
      configId: parseInt(configId),
      sections: sections,
    });

  templateCopyInstructions = (
    configId,
    closestMatchConfigId,
    missingSections,
  ) =>
    this.JMPost('/v1/template/copyInstructions', {
      configId: parseInt(configId),
      closest_match_configId: closestMatchConfigId,
      sections: missingSections,
    });

  sendFeedback = (feedbackData) =>
    this.JMPost('/v1/config/feedback', feedbackData);
  editBuild = (buildData) => this.JMPost('/v1/config/edit/build', buildData);

  // Per-section Learn for the whole note in one call ([{section, usersEdits}]).
  // It used to be one /v1/template/learn call per section, and on notes with
  // many edited sections some of them outlived API Gateway's ~29s limit
  // (COM-586). The backend answers sections with nothing to learn right away
  // and learns the rest in a background job that we poll. Resolves to
  // {sections: {[section]: {status, body}}} or {error}.
  learnSections(conversationId, configId, sections, instructionChangeEventId) {
    return this.JMPost('/v1/template/learnSections', {
      conversationId,
      configId,
      sections,
      instructionChangeEventId,
    }).then((kickoff) => {
      if (!kickoff || !kickoff.jobId) return kickoff;
      return this._pollLearnJob(
        '/v1/template/learnSections/status',
        conversationId,
        kickoff.jobId,
      ).then((result) =>
        result && result.sections
          ? { sections: { ...kickoff.sections, ...result.sections } }
          : result,
      );
    });
  }

  // Whole-note learn: one call with the full final note ({section: note}).
  // The backend detects changed sections, learns each with full-note context,
  // and runs a single per-user accuracy pass loop over the whole note.
  //
  // This chains several sequential GPT calls per changed section, so on the
  // backend it now runs as an async job (kicked off here, then executed by a
  // self-invoked worker Lambda) instead of inline, to avoid tripping API
  // Gateway's ~29s response timeout on notes with multiple changed sections.
  // We poll /v1/template/learnNote/status here so callers can keep doing
  // `await apiClient.learnNote(...)` and get the same result shape as before
  // ({result, sections, skipped, learnLoop} or {error}).
  learnNote(
    conversationId,
    configId,
    usersEdits,
    instructionChangeEventId = crypto.randomUUID(),
  ) {
    return this.JMPost('/v1/template/learnNote', {
      conversationId,
      configId,
      usersEdits,
      instructionChangeEventId,
    }).then((kickoff) => {
      if (!kickoff || !kickoff.jobId) {
        // Fast-fail path (bad args, nothing changed, auth/access error) -
        // the kickoff endpoint already returned the final shape.
        return kickoff;
      }
      return this._pollLearnJob(
        '/v1/template/learnNote/status',
        conversationId,
        kickoff.jobId,
      );
    });
  }

  // Polls a learn job's status endpoint until the job reaches a terminal
  // state (done/error), or we give up after ~5 minutes.
  _pollLearnJob(
    statusPath,
    conversationId,
    jobId,
    { intervalMs = 2500, timeoutMs = 5 * 60 * 1000 } = {},
  ) {
    const deadline = Date.now() + timeoutMs;
    const poll = () =>
      this.JMPost(statusPath, {
        conversationId,
        jobId,
      }).then((status) => {
        if (!status || status.error) {
          // job/conversation lookup failed, or session/ownership error
          return status || { error: 'Learn status check failed' };
        }
        if (status.status === 'done') {
          return status.result;
        }
        if (status.status === 'error') {
          return { error: status.error, skipped: status.skipped };
        }
        // still pending
        if (Date.now() > deadline) {
          return { error: 'Learning timed out waiting for a result' };
        }
        return new Promise((resolve) => setTimeout(resolve, intervalMs)).then(
          poll,
        );
      });
    return poll();
  }

  // Agentic Learn (features.signed_note_learner === 'agentic_learn'): hand one
  // clinician action to the agent. kind is 'learn' | 'instruction_assistant' |
  // 'thumbs_down'. Returns {accepted: true, triggerId, status} or
  // {accepted: false, reason}. The run itself is invisible to the clinician.
  triggerAgenticLearn = (configId, conversationId, kind, context) =>
    this.JMPost('/v1/agenticLearn/trigger', {
      configId,
      conversationId,
      kind,
      context,
    });

  // The finished note Learn was clicked on, kept as a Re-Learn My Style example
  // whether or not Learn changed anything (LambdaAPI Endpoints/RelearnStyle.py).
  saveLearnExample = (example) => this.JMPost('/v1/notes/learnExample', example);

  // Re-Learn My Style: eligibility for the opened template, then an async run whose
  // progress is polled with getRelearnStyleStatus.
  getRelearnStyleSummary = (configId) => this.JMPost('/v1/notes/relearnStyle/summary', { configId });
  startRelearnStyle = (configId, sections) => this.JMPost('/v1/notes/relearnStyle/start', { configId, sections });
  getRelearnStyleStatus = () =>
    this.JMPost('/v1/notes/relearnStyle/status', {}).then((response) => {
      if (response?.job?.status === 'done') {
        // The run added templates and may have moved the heart.
        cacheManager.remove('user_configs');
        cacheManager.remove('user_stats');
      }
      return response;
    });

  // Chat
  getChatHistory = (conversationID) =>
    this.JMPost('/v1/conversation/chat/history', {
      conversationId: conversationID,
    });

  askChatQuestion = (conversationID, question) =>
    this.JMPost('/v1/conversation/chat/question', {
      conversationId: conversationID,
      chatquestion: question,
    });

  // Streaming variant: tokens are pushed to the given WebSocket connection
  // (see api/ChatStream.js) while this request is in flight; the resolved
  // response body is still the authoritative answer.
  askChatQuestionStream = (conversationID, question, connectionId) =>
    this.JMPost('/v1/conversation/chat/question/stream', {
      conversationId: conversationID,
      chatquestion: question,
      connectionId: connectionId,
    });

  // Agentic chat (COM-536): one model step of a turn whose tools run in the
  // visit view. Returns {toolCalls, content} to run, or the final {response}.
  // turnId is the same on every step of a turn, so a retried step is saved once.
  chatAgentStep = (conversationID, question, tools, turnMessages, actions, context, turnId) =>
    this.JMPost('/v1/conversation/chat/agent', {
      conversationId: conversationID,
      chatquestion: question,
      tools: tools,
      turnMessages: turnMessages,
      actions: actions,
      context: context,
      turnId: turnId,
    });

  clearChatHistory = (conversationID) =>
    this.JMPost('/v1/conversation/chat/clear', {
      conversationId: conversationID,
    });

  // Conversation
  getSchedule = (payload = {}) => this.JMPost('/partners/schedule', payload);

  patchRaintreeEMR = (conversationId) =>
    this.JMPost('/partners/raintree/patch', {
      conversationId: conversationId,
    });

  mapEMR = (conversationId) =>
    this.JMPost('/partners/raintree/map', {
      conversationId: conversationId,
    });

  emrBuild = (configID) =>
    this.JMPost('/partners/build', {
      configID: configID,
    });

  newConversation(patientName, configId = null) {
    var body = {};
    if (patientName !== '') {
      body = { patientId: patientName };
    } else {
      body = {};
    }
    if (configId) {
      body['configId'] = parseInt(configId);
    }
    const rsp = this.JMPost('/v1/conversation/new', body);
    if (!this.active) {
      this.creditsRemaining--;
    }
    return rsp;
  }
  getConversationQuestion(
    conversationID,
    questionKey,
    questionValue,
    type,
    choices,
    existingNote = null,
    downgraded,
    structure = {},
    parallelSections = null,
  ) {
    const data = {
      conversationId: conversationID,
      questionKey: questionKey,
      questionValue: questionValue,
      type: type,
      choices: choices,
      previousNote: existingNote,
      downgraded: downgraded,
      structure: structure,
      user_note_rules: this.userNoteRules,
    };
    if (parallelSections?.length) data.parallel_sections = parallelSections;
    return this.JMPost('/v1/conversation/question', data);
  }
  getConversationQuestions = (
    conversationID,
    questionsData,
    type,
    downgraded,
  ) =>
    this.JMPost('/v1/conversation/questions', {
      conversationId: conversationID,
      questions: questionsData,
      type: type,
      downgraded: downgraded,
      user_note_rules: this.userNoteRules,
    });
  // Save in click order so an older rating cannot overwrite a newer one.
  saveComprehendFeedback = (conversationID, feedback) => {
    const payload = { conversationId: conversationID, feedback: { ...feedback } };
    const saved = (this.feedbackSaveInFlight || Promise.resolve()).then(() =>
      this.JMPost('/v1/notes/feedback', payload),
    );
    // A failed save must not prevent the next answer from being sent.
    this.feedbackSaveInFlight = saved.catch(() => {});
    return saved;
  };
  deleteConversation(conversationId) {
    return this.JMPost('/v1/conversation/delete', {
      conversationId: conversationId,
    }).then((data) => {
      // Remove it from our local copy of the conversations
      this.conversations = this.conversations.filter(
        (conversation) => conversation.conversationId !== conversationId,
      );
      return data;
    });
  }
  getConversationDetails(conversationID) {
    // check that conversationID is valid
    if (typeof conversationID !== 'number' || conversationID <= 0) {
      return new Promise((resolve, reject) =>
        reject('Expected: Invalid Conversation ID'),
      );
    }

    const data = {
      conversationId: conversationID,
    };
    return this.JMPost('/v1/conversation/details', data);
  }
  editConversation(conversationID, fieldName, fieldValue) {
    const data = {
      conversationId: conversationID,
      fieldName: fieldName,
      value: fieldValue,
    };
    return this.JMPost('/v1/conversation/edit', data);
  }
  comprehendConversation(conversationID, questionKey, wait, downgraded) {
    const data = {
      conversationId: conversationID,
      questionKey: questionKey,
      waitForTranscription: wait,
      downgraded: downgraded,
      user_note_rules: this.userNoteRules,
    };
    // console.log(data);
    return this.JMPost('/v1/conversation/comprehend', data);
  }
  scoreConversation(
    conversationID,
    existingValues = null,
    emrMetadata = null,
  ) {
    const data = {
      conversationId: conversationID,
      existingValues: existingValues,
    };
    // The EMR's own note metadata (CORA Sapphire's noteMetadata blob). Its
    // isMC / isWC / isPeds flags pick which compliance rule set grades the note.
    // Only send it when we actually scraped something, so accounts on EMRs that
    // publish nothing keep the exact request shape they had before.
    if (emrMetadata && typeof emrMetadata === 'object') {
      data.emrMetadata = emrMetadata;
    }
    // console.log(data);
    return this.JMPost('/v1/conversation/score', data);
  }
  getCodingRecommendations(conversationID, params = {}) {
    const data = {
      conversationId: conversationID,
      payer_key: params.payer_key || null,
      note_type: params.note_type || null,
      extended_codes: params.extended_codes || null,
      existing_values: params.existing_values ?? null,
    };
    return this.JMPost('/v1/conversation/coding/recommendations', data);
  }
  getCodingRecommendationsStatus(conversationID) {
    return this.JMPost('/v1/conversation/coding/recommendations/status', {
      conversationId: conversationID,
    });
  }

  getBillingRules() {
    return this.JMPost('/v1/billing-rules/get', {});
  }

  saveBillingRules(payer) {
    return this.JMPost('/v1/billing-rules/save', payer);
  }

  deletePayer(payerKey) {
    return this.JMPost('/v1/billing-rules/delete-payer', {
      payer_key: payerKey,
    });
  }
  addConversationAudio(conversationID, audioData, duration) {
    const data = {
      conversationId: conversationID,
      audio: audioData,
      duration: duration,
    };
    return this.JMPost('/v1/conversation/audio/add', data);
  }
  // Stateless: transcribe a short clip and get the text back (for dictation).
  // Does not touch any conversation transcript.
  transcribeAudio(audioData) {
    return this.JMPost('/v1/transcribe', { audio: audioData });
  }
  addConversationAudioMultiple(
    conversationIDs,
    audioData,
    duration,
    previousLastSpeaker = null,
  ) {
    const data = {
      conversationIds: conversationIDs,
      audio: audioData,
      duration: duration,
    };
    if (previousLastSpeaker != null && previousLastSpeaker !== '') {
      data.previousLastSpeaker = previousLastSpeaker;
    }
    return this.JMPost('/v1/conversation/audio/addMultiple', data);
  }
  // MultiRecord++ sliding-window path. `context` is the tail of what the backend
  // already attributed (see agent/multiRecordContext.js); it comes back trimmed
  // in the response to send with the next chunk.
  addConversationAudioSegmented(
    conversationIDs,
    audioData,
    duration,
    context,
    session,
    allowUnknown = false,
  ) {
    const data = {
      conversationIds: conversationIDs,
      audio: audioData,
      duration: duration,
    };
    // Lines the backend cannot place come back in `unplaced`, on no visit.
    if (allowUnknown) data.allowUnknown = true;
    // Diagnostic only — lets the backend log this chunk as part of a recording
    // session so attribution can be replayed offline. Nothing here reaches the
    // model or changes attribution.
    if (session && session.recordingSessionId) {
      data.recordingSessionId = session.recordingSessionId;
      data.chunkSeq = session.chunkSeq;
      data.chunkOffsetMs = session.chunkOffsetMs;
      data.visitJoins = session.visitJoins;
    }
    if (Array.isArray(context) && context.length) {
      data.context = context;
    }
    return this.JMPost('/v1/conversation/audio/addSegmented', data);
  }
  summarizeMultiRecordReview(lines) {
    return this.JMPost('/v1/conversation/multirecord/reviewSummaries', { lines });
  }
  appendMultiRecordReviewLines(conversationId, lines) {
    return this.JMPost('/v1/conversation/multirecord/appendReviewLines', { conversationId, lines });
  }
  saveMultiRecordReviewOutcome(finish, lines) {
    return this.JMPost('/v1/conversation/multirecord/reviewOutcome', { finish, lines });
  }
  addConversationAudioLarge(conversationID, audioData, duration, fileType) {
    if (!fileType) {
      fileType = 'webm';
    }
    return this.JMPost('/v1/conversation/audio/requesturl', {
      conversationId: conversationID,
      fileType: fileType,
    }).then((data) => {
      if ('error' in data) {
        console.log('Failed to get URL');
        // console.log(data)
        return new Promise((resolve, reject) => reject('Failed to get URL'));
      }
      const url = data['url'];
      // console.log(url)
      return fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': `audio/${fileType}` },
        body: audioData,
      }).then((data) => {
        // console.log("Data from PUT")
        // console.log(data)
        if (!data.ok) {
          console.log('Failed to PUT');
          console.log(data);
          return new Promise((resolve, reject) => reject('Failed to PUT'));
        }
        data = {
          conversationId: conversationID,
          url: url,
          duration: duration,
        };
        return this.JMPost('/v1/conversation/audio/addfromurl', data);
      });
    });
  }
  getAffiliateResponse = () => this.JMPost('/v1/affiliate/details', {});
  haveSessionKey() {
    // Check if there is a session key in storage
    if (
      this.sessionKey == null ||
      this.sessionKey === 'undefined' ||
      this.sessionKey === 'null' ||
      this.sessionKey === 'NaN' ||
      isNaN(this.sessionKey)
    ) {
      // return false in a promise
      return false;
    }
    return true;
  }

  checkExpiration() {
    if (!this.haveSessionKey()) {
      // return a reject promise
      return new Promise((resolve, reject) => resolve(false));
    }
    return this.getExpiration({}).then((data) => {
      if ('error' in data) {
        console.log('Expiration Failed to Update');
        // console.log(data)
        return false;
      }
      // console.log("Got Expiration")
      // console.log(data)
      // Compare the expiration date to the current date
      // If the expiration date is in the past then return false
      // If the expiration date is in the future then return true
      // If the expiration date is null then return false

      // expiration = new Date(data["expiration"])
      // console.log(expiration)
      // console.log(Date.now())
      // if (expiration < Date.now()) {
      //     console.log("Expired")
      //     // Check if the subscription is FREE
      //     if (data["subscription"] === "FREE") {
      //         console.log("Free")
      //         return true;
      //     }
      //     return false
      // }
      // console.log("Not Expired")

      return true;
    });
  }

  actionAllowed() {
    // Action is allowed if active subscription or free subscription with credits available
    // Check if the user is active
    if (this.active) {
      return true;
    }
    // Check if the user has credits remaining
    if (this.creditsRemaining > 0) {
      return true;
    }

    return false;
  }

  updateProfile() {
    if (!this.haveSessionKey()) {
      // return a reject promise
      return new Promise((resolve, reject) => reject('No Session Key'));
    }
    if (this.name != null) {
      // Already got the profile
      return new Promise((resolve, reject) => resolve(false));
    }
    // Update the local copy of the user profile
    return this.getStats({}).then((data) => {
      // console.log("Got Stats")
      // console.log(data)

      if ('error' in data) {
        console.log('Profile Failed to Update');
        // console.log(data)
        return false;
      }

      this.name = data['name'];
      this.minutesTranscribed = data['minutesTranscribed'];
      this.conversationsHad = data['conversationsHad'];
      this.patientsSeen = data['patientsHelped'];
      this.minutesSaved = data['minutesSaved'];
      this.referralCode = data['myReferralCode'];
      this.EMR = data['EMR'];
      this.expiration = data['expiration'];
      this.trialEnd = data['trialEnd'];
      this.subscription = data['subscription'];
      this.active = data['active'];
      this.creditsRemaining = data['creditsRemaining'];
      this.email = data['email'];
      this.isBatchingEnabled = data['isBatchingEnabled'];
      this.modifyExisting = data['modifyExisting'];
      this.isDev = data['isDev'];
      // Onboarding checklist progress (see components/Onboarding). Missing on
      // backends that predate it — treat as empty, never undefined.
      this.onboarding = data['onboarding'] || {};
      // Account creation date: the checklist shows only to accounts created
      // after the playground onboarding launched (it read this before anything
      // set it, so every account was treated as new).
      this.createdAt = data['createdAt'] || null;
      // Old/new integration switch window (COM-561), null when none is open.
      this.integrationSwitch = data['integrationSwitch'] || null;
      this.is_beta = data['isBeta'];
      this.showAllEMRTemplates = data['showAllEMRTemplates'];
      this.showComplianceScore = data['showComplianceScore'];
      this.force_mic_drop = data['forceMicDrop'];
      this.empowerNewVersion = data['empowerNewVersion'] ?? false;
      this.features = {
        ...FRONTEND_FEATURE_DEFAULTS,
        ...(data['features'] || {}),
      };
      this.newRaintreeIntegration = this.features['new_rt_integration'];
      this.newPromptIntegration = this.features['new_prompt_integration'];
      this.coraRom = this.features['cora_rom'];
      this.newWebPTSoap2Integration = this.features['new_webpt_soap2_integration'];
      this.webpt2ChromeFreezeFix = this.features['webpt2_chrome_freeze_fix'];
      this.userNoteRules = this.features['user_note_rules'];
      // Learn++: whole-note async learn (/v1/template/learnNote) vs the
      // legacy per-section /v1/template/learn. Same rollout pattern as
      // new_rt_integration — flip the feature per user from the admin panel.
      this.learnPlusEnabled = this.features['learn_plus'];
      this.scorecard = this.features['scorecard']
      this.patientMatchWarning = this.features['patient_match_warning']
      this.scoreAllNotesEnabled = this.features['score_all_notes']
      // Vendor Apps enabled on the group (docs/vendor-apps-contract.md) —
      // {id, name, embedOrigin, launchUrl, enabled}. Consumed by the Apps tab.
      // Set BEFORE the profile callbacks fire so listeners see it.
      this.vendorApps = Array.isArray(data['vendor_apps']) ? data['vendor_apps'] : [];
      // A backend without the Vendor Apps routes omits the key entirely; the store
      // then skips /v1/vendorApps/list instead of failing it on every visit. Stays
      // undefined until the profile has loaded — vendor calls wait for that, because
      // the backend switch (dev/prod) below only happens once the profile is in.
      // The answer is only authoritative when the backend that served this profile
      // is the one we end up talking to; a dev switching from the default backend
      // gets the default's profile, so treat "switched" as supported.
      const profileFrom = this.backendEnv;
      this.applyBackendEnv(this.readBackendPreference());
      this.vendorAppsSupported =
        this.backendEnv === profileFrom ? Object.prototype.hasOwnProperty.call(data, 'vendor_apps') : true;
      // Fire any registered callbacks now that profile/features are loaded
      if (this._profileUpdateCallbacks.length) {
        this._profileUpdateCallbacks.forEach((cb) => {
          try {
            cb(this);
          } catch (e) {
            console.error('Profile update callback failed', e);
          }
        });
      }
      this.isWebPTUser = data['isWebPTUser'];
      this.groupSubscriptionId = data['group_subscription_id'];

      const CONFLUENT_GROUP_ID = 1017803860;
      this.isConfluentUser = this.groupSubscriptionId == CONFLUENT_GROUP_ID;
      if (this.groupSubscriptionId != CONFLUENT_GROUP_ID) {
        // Appcues user identification
        if (window.Appcues) {
          window.Appcues.identify(this.email?.toLowerCase(), {
            email: this.email,
            name: this.name,
            planTier: this.subscription,
            emr: this.EMR,
            expiration: this.expiration,
            isDev: this.isDev,
            conversationsHad: this.conversationsHad,
            patientsSeen: this.patientsSeen,
            minutesTranscribed: this.minutesTranscribed,
            active: this.active,
            account_created_at:
              data['createdAt']?.match(/\d{4}-\d{2}-\d{2}/)?.[0],
            user_role: data['role'],
            ...Object.fromEntries(
              Object.entries(this.features)
                .filter(
                  ([, value]) =>
                    typeof value === 'boolean' ||
                    typeof value === 'number' ||
                    (typeof value === 'string' && value.length > 0),
                )
                .map(([feature, value]) => [
                  `feature_${feature}`,
                  value,
                ]),
            ),
          });
          if (this.groupSubscriptionId) {
            window.Appcues.group(this.groupSubscriptionId, {});
          }
        }
      }

      // Session replay boots here rather than at app mount: this is the first
      // point where the profile (and therefore EMR / dev flags) is loaded, and
      // it runs on every surface that loads a profile — web app, side panel and
      // embedded iframe — without touching three different entry points.
      // Lazily imported so the rrweb chunk is never fetched for a user the
      // server declines to record, and awaited-not-blocking so a slow policy
      // call can't delay the app.
      import('./replay')
        .then(({ startSessionReplay }) => startSessionReplay(this))
        .catch((e) => console.log('Session replay unavailable', e));

      return true;
    });
  }

  updatePatients() {
    if (!this.haveSessionKey()) {
      // return a reject promise
      return new Promise((resolve, reject) => reject('No Session Key'));
    }
    // Update the local copy of the patients
    // console.log("Getting Patients")
    return this.getUserGroups({}).then((data) => {
      if ('error' in data) {
        console.log('Patients Failed to Update');
        this.patients = [];
        // console.log(data)
        return data;
      }

      this.patients = data['groups'];
      // console.log("Got Patients")
      // console.log(this.patients)

      // Replace all lastUsed fields with a human readable date
      this.patients.forEach((patient) => {
        patient.lastUsed = new Date(patient.lastUsed + 'Z').toLocaleString();
        patient.dob = new Date(patient.dob).toLocaleDateString();
      });

      try {
        this.patients.sort((a, b) => {
          let aLastName = String(a.lastName || '').toLowerCase();
          let bLastName = String(b.lastName || '').toLowerCase();
          let aFirstName = String(a.firstName || '').toLowerCase();
          let bFirstName = String(b.firstName || '').toLowerCase();

          if (aLastName === bLastName) {
            // If lastNames are the same, sort by firstName
            return aFirstName.localeCompare(bFirstName);
          }
          // Otherwise, sort by lastName
          return aLastName.localeCompare(bLastName);
        });
      } catch (e) {
        console.error('Sorting error:', e);
      }

      return true;
    });
  }

  updateConversations(group = null, startDate = null, endDate = null) {
    if (!this.haveSessionKey()) {
      // return a reject promise
      return new Promise((resolve, reject) => reject('No Session Key'));
    }
    // Update the local copy of the conversations
    // console.log("Getting Conversations")
    return this.getUserConversations(group, startDate, endDate).then((data) => {
      if ('error' in data) {
        console.log('Conversations Failed to Update');
        // console.log(data)
        this.conversations = [];
        return data;
      }

      // console.log("Got Conversations")
      // console.log(data)
      this.conversations = data['conversations'];

      // loop over the conversations and replace .lastUsed with new Date(this.conversations[i].lastUsed)
      var i = 0;
      for (i = 0; i < this.conversations.length; i++) {
        this.conversations[i].lastUsed = new Date(
          this.conversations[i].lastUsed + 'Z',
        );
      }

      // Sort the conversations by createdAt descending
      this.conversations.sort((a, b) => {
        const aDate = a.createdAt ? new Date(a.createdAt + 'Z') : new Date(0);
        const bDate = b.createdAt ? new Date(b.createdAt + 'Z') : new Date(0);
        return bDate > aDate ? 1 : -1;
      });

      // Using the data list populate a list of patients with the patient name, # of conversations, and last used
      var patients = {};

      // for convo in this.conversations {
      for (i = 0; i < this.conversations.length; i++) {
        var convo = this.conversations[i];
        if (convo.group in patients) {
          patients[convo.group].conversations += 1;
          patients[convo.group].conversationIds.push(convo.conversationId);
          if (convo.lastUsed > patients[convo.group].lastUsed) {
            patients[convo.group].lastUsed = convo.lastUsed;
          }
        } else {
          patients[convo.group] = {
            conversations: 1,
            lastUsed: convo.lastUsed,
            conversationIds: [convo.conversationId],
          };
        }
      }

      // For both the conversations and patients list remove the lastUsed field and replace it with a human readable date
      this.conversations.forEach((conversation) => {
        conversation.lastUsed = conversation.lastUsed.toLocaleString();
      });

      return true;
    });
  }

  // Admin Dashboard
  adminSearchGroupSubscription = (subscriptionEmail) =>
    this.JMPost('/v1/admin/searchGroupSubscription', {
      subscriptionEmail: subscriptionEmail,
    });
  adminSearchGroupSubscriptionByOrg = (organizationName) =>
    this.JMPost('/v1/admin/searchGroupSubscription', {
      organizationName: organizationName,
    });
  adminListGroupSubscriptions = (page = 1, pageSize = 25) =>
    this.JMPost('/v1/admin/listGroupSubscriptions', { page, pageSize });
  adminRemoveUserFromGroup = (subscriptionEmail, email) =>
    this.JMPost('/v1/admin/removeUserFromGroup', {
      subscriptionEmail,
      email,
    });
  adminChangeUserRole = (subscriptionEmail, email, role) =>
    this.JMPost('/v1/admin/changeUserRole', {
      subscriptionEmail,
      email,
      role,
    });
  adminCreateGroupSubscription = (
    subscriptionEmail,
    seats,
    expiration,
    emails,
    groups,
    organizationName,
  ) =>
    this.JMPost('/v1/admin/createGroupSubscription', {
      subscriptionEmail: subscriptionEmail,
      seats: seats,
      expiration: expiration,
      emails: emails,
      groups: groups,
      organizationName: organizationName,
    });
  adminUpateGroupSubscription = (
    subscriptionEmail,
    seats,
    expiration,
    emails,
    groups,
    organizationName,
  ) =>
    this.JMPost('/v1/admin/updateGroupSubscription', {
      subscriptionEmail: subscriptionEmail,
      seats: seats,
      expiration: expiration,
      emails: emails,
      groups: groups,
      organizationName: organizationName,
    });
  adminDeleteGroupSubscription = (subscriptionId) =>
    this.JMPost('/v1/admin/deleteGroupSubscription', {
      subscriptionId,
    });
  adminMakeUserGroupAdmin = (email) =>
    this.JMPost('/v1/admin/makeUserGroupAdmin', { email: email });
  adminSetExpiration = (email, code, expiration, isAuth) =>
    this.JMPost('/v1/admin/setExpiration', {
      email: email,
      code: code,
      expiration: expiration,
      isAuth: isAuth,
    });
  adminCreatePromo = (code, expiration, days) =>
    this.JMPost('/v1/admin/createPromo', {
      code: code,
      expiration: expiration,
      days: days,
    });
  adminSearchUser = (searchKey) =>
    this.JMPost('/v1/admin/searchUser', { searchKey: searchKey });
  adminGetUserConfigs = (email) =>
    this.JMPost('/v1/admin/getUserConfigs', { email: email });
  adminGetUserDetails = (email) =>
    this.JMPost('/v1/admin/getUserDetails', { email: email });
  adminSetUserDetails = (email, key, value) =>
    this.JMPost('/v1/admin/setUserSetting', {
      email: email,
      key: key,
      value: value,
    });
  adminChangeEmail = (old_email, new_email) =>
    this.JMPost('/v1/admin/changeEmail', {
      oldEmail: old_email,
      newEmail: new_email,
    });
  adminSetFeature = (emails, featureName, value) =>
    this.JMPost('/v1/admin/setFeature', {
      emails: emails || [],
      featureName: featureName,
      value: value,
    });
  adminImpersonateUser = (mfa_code, target_email, replay_token = null) =>
    this.JMPost('/v1/admin/impersonateUser', {
      mfa_code,
      target_email,
      replay_token,
    });
  adminGetBackendFeatures = () =>
    this.JMPost('/v1/admin/getBackendFeatures', {});
  adminGetUsersFeatures = (email) =>
    this.JMPost('/v1/admin/getUsersFeatures', { email });
  adminListRollouts = () => this.JMPost('/v1/admin/rollout/list', {});
  adminSaveRollout = (rollout) =>
    this.JMPost('/v1/admin/rollout/save', rollout);
  adminSetRolloutStatus = (flag, action) =>
    this.JMPost('/v1/admin/rollout/setStatus', { flag, action });
  adminRollbackRollout = (flag) =>
    this.JMPost('/v1/admin/rollout/rollback', { flag });
  adminGetRolloutUsers = (flag) =>
    this.JMPost('/v1/admin/rollout/users', { flag });
  adminPreviewInstructionPush = (push) =>
    this.JMPost('/v1/admin/instructionPush/preview', push);
  adminApplyInstructionPush = (push, mfa_code) =>
    this.JMPost('/v1/admin/instructionPush/apply', { ...push, mfa_code });
  adminGetInstructionAssistantPrompts = () =>
    this.JMPost('/v1/admin/instructionAssistantPrompts/get', {});
  adminSaveInstructionAssistantPrompt = (key, prompt) =>
    this.JMPost('/v1/admin/instructionAssistantPrompts/save', { key, prompt });
  adminDeleteInstructionAssistantPrompt = (key) =>
    this.JMPost('/v1/admin/instructionAssistantPrompts/delete', { key });
  adminDetailUsage = (t1, t2, users, getGroupUsers) =>
    this.JMPost('/v1/usage/admin', {
      t1: t1,
      t2: t2,
      users: users,
      getGroupUsers: Boolean(getGroupUsers),
    });
  bulkAddLabels = (data) => this.JMPost('/v1/admin/bulkAddLabels', data);
  removeLabelFromUser = (data) =>
    this.JMPost('/v1/admin/removeLabelFromUser', data);
  removeLabelFromAllUsers = (data) =>
    this.JMPost('/v1/admin/removeLabelFromAllUsers', data);
  modifyPeople = (data) => this.JMPost('/pipedrive/modifyPeople', data);
  logMessage = (errorLevel, message, replayId = null) => {
    try {
      if (!message.includes('No response received')) {
        this.JMPost('/v1/log/error', {
          error: message,
          // Only sent when a replay is actually running; the endpoint treats it
          // as optional, so old bundles posting without it keep working.
          ...(replayId ? { replayId } : {}),
        });
      } else {
        console.log('Skipping error - no response received.');
      }
    } catch (error) {
      // Handle any errors that occur during the post
      console.log(`Failed to log message: ${message}. Error: ${error}`);
    }
  };

  // ── Session replay (see api/replay/) ───────────────────────────────────────
  //
  // The browser never decides whether to record; it asks. See
  // Endpoints/SessionReplay.py decide_replay_policy for the rules.
  getReplayPolicy = (context = {}) => this.JMPost('/v1/replay/policy', context);

  getReplayChunkUrl = (replayId, sequence, contentType, artifact = 'events', recovered = false) =>
    this.JMPost('/v1/replay/chunk/requesturl', {
      replayId,
      sequence,
      contentType,
      artifact,
      // A tail carried over from a previous session (see replay/chunkCache.js):
      // this upload is not "now", so the server must not treat it as activity.
      ...(recovered ? { recovered: true } : {}),
    });

  finalizeReplay = (summary) => this.JMPost('/v1/replay/finalize', summary);

  /**
   * Upload the diagnostic network log alongside a replay's events and return
   * its S3 key (or null). Uses the same presigning endpoint with
   * `artifact: 'har'`, which files it under a distinct name so the viewer never
   * mistakes it for an events chunk.
   */

  // Dev-only replay retrieval (SessionReplayViewer). Server re-checks dev.
  /**
   * Viewer token for the replay screens.
   *
   * An authenticator code is exchanged once for this (see unlockReplayViewer);
   * it is then sent with every replay admin call and checked server-side each
   * time. The browser is never the thing deciding whether someone may watch a
   * recording of a patient chart — and unlike sending the raw code, there is
   * nothing here an attacker could guess.
   */
  replayToken = null;

  setReplayToken = (token) => {
    this.replayToken = token || null;
  };

  /** Exchange an authenticator code for a 12-hour viewer token. */
  unlockReplayViewer = (mfaCode) =>
    this.JMPost('/v1/admin/replay/unlock', { mfa_code: mfaCode });

  /** Attach the viewer token to a replay admin request. */
  withReplayMfa = (body = {}) => ({ ...body, replay_token: this.replayToken });

  searchReplays = (query) =>
    this.JMPost('/v1/admin/replay/search', this.withReplayMfa(query));
  getReplay = (replayId) =>
    this.JMPost('/v1/admin/replay/get', this.withReplayMfa({ replayId }));
  enableReplayForUser = (userEmail, hours = 24) =>
    this.JMPost('/v1/admin/replay/enable', this.withReplayMfa({ userEmail, hours }));

  // Dev-only multi-record attribution viewer. Shares the replay viewer's
  // unlock token on purpose: one second factor for both PHI screens. Server
  // re-checks dev and the token on every call.
  searchMultiRecordSessions = (query) =>
    this.JMPost('/v1/admin/multirecord/sessions', this.withReplayMfa(query));
  getMultiRecordSession = (sessionId) =>
    this.JMPost('/v1/admin/multirecord/session', this.withReplayMfa({ sessionId }));

  /** Attribution viewer stats + session list: counts and decisions, no transcript text. */
  getMultiRecordStats = (filters) => this.JMPost('/v1/admin/multirecord/stats', filters);

  getMultiRecordReviewLines = (sessionId) =>
    this.JMPost('/v1/admin/multirecord/reviewLines', { sessionId });

  getErrorVideoUrl = () => this.JMPost('v1/error/video/requesturl', {});
  sendSlackMessage = (description, channelName, userInfo, url, file) =>
    this.JMPost('v1/sendSlackMessage', {
      description,
      channelName,
      userInfo,
      url,
      file,
    });

  addErrorVideo(videoData, description, channelName, userInfo, logFile) {
    return this.JMPost('v1/error/video/requesturl', {}).then((data) => {
      if ('error' in data) {
        console.log('Failed to get URL');
        return Promise.reject('Failed to get URL');
      }

      const url = data['url'];
      return fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': `video/webm` },
        body: videoData,
      }).then((response) => {
        if (!response.ok) {
          console.log('Failed to PUT');
          console.log(response);
          return Promise.reject('Failed to PUT');
        }
        const cleanUrl = url.replace(/\?.*$/, '');
        const videoInfo = {
          description,
          channelName,
          userInfo,
          url: cleanUrl,
          self_test: false,
          file: logFile,
        };
        return this.JMPost('v1/sendSlackMessage', videoInfo);
      });
    });
  }

  createPartnerSecret(secretKey, value, force) {
    return this.JMPost('/v1/admin/createSecret', {
      secretKey: secretKey,
      value: value,
      force: force,
    });
  }

  linkPartnerSecret(secretKey, groupOrUser) {
    return this.JMPost('/v1/admin/linkSecret', {
      secretKey: secretKey,
      groupOrUser: groupOrUser,
    });
  }

  checkPartnerConnection(secretKey) {
    return this.JMPost('/partners/admin/checkConnection', {
      secretKey: secretKey,
    });
  }

  searchConfigsLibrary = (emr, noteType, profession, userId) =>
    this.JMPost('/v1/configsLibrary/search', {
      emr: emr,
      noteType: noteType,
      profession: profession,
      userId: userId,
    });

  copyLibraryConfig = (libraryConfigId, emr) =>
    this.JMPost('/v1/configsLibrary/copy/libraryConfig', {
      libraryConfigId: parseInt(libraryConfigId),
      emr: emr,
    });

  getLibraryConfigDetails = (libraryConfigId, emr) =>
    this.JMPost('/v1/configsLibrary/config/details', {
      libraryConfigId: parseInt(libraryConfigId),
      emr: emr,
    });

  devDemote = (libraryConfigId, emr) =>
    this.JMPost('/v1/configsLibrary/dev/demote/libraryConfig', {
      libraryConfigId: parseInt(libraryConfigId),
      emr: emr,
    });

  devUpdateLibraryConfigReplyType = (
    libraryConfigId,
    emr,
    replyType,
    sectionName,
  ) =>
    this.JMPost('/v1/configsLibrary/dev/updateReplyType/libraryConfig', {
      libraryConfigId: parseInt(libraryConfigId),
      emr: emr,
      sectionName: sectionName,
      replyType: replyType,
    });

  devPromote = (configID) =>
    this.JMPost('/v1/configsLibrary/promote/config', {
      configId: parseInt(configID),
    });

  devViewPromotedTemplate = (configID) =>
    this.JMPost('/v1/configsLibrary/originalConfig/details', {
      originalConfigId: parseInt(configID),
    });

  editLibraryConfigInstruction = (
    libraryConfigId,
    emr,
    instructionKey,
    instructionValue,
  ) =>
    this.JMPost('/v1/configsLibrary/dev/updateInstruction/libraryConfig', {
      libraryConfigId: parseInt(libraryConfigId),
      emr: emr,
      instructionKey: instructionKey,
      instructionValue: instructionValue,
    });

  addLibraryConfigSection = (libraryConfigId, emr, sectionName) =>
    this.JMPost('/v1/configsLibrary/dev/addSection/libraryConfig', {
      libraryConfigId: parseInt(libraryConfigId),
      emr: emr,
      sectionName: sectionName,
    });

  deleteLibraryConfigSection = (libraryConfigId, emr, sectionName) =>
    this.JMPost('/v1/configsLibrary/dev/deleteSection/libraryConfig', {
      libraryConfigId: parseInt(libraryConfigId),
      emr: emr,
      sectionName: sectionName,
    });

  // Compliance Guidelines
  getComplianceUrls = (fileNames) =>
    this.JMPost('/v1/compliance/guidelines/getUrls', {
      fileNames: fileNames,
    });

  addComplianceDocuments = (noteType, urls, title) =>
    this.JMPost('/v1/compliance/guidelines/addDocuments', {
      noteType: noteType,
      urls: urls,
      title: title,
    });

  extractComplianceGuidelines = (guidelineId, noteType = null) => {
    const params = { guidelineId: guidelineId };
    if (noteType) params.noteType = noteType;
    return this.JMPost('/v1/compliance/guidelines/extract', params);
  };

  getComplianceGuidelines = (noteType = null) => {
    const params = noteType ? { noteType: noteType } : {};
    return this.JMPost('/v1/compliance/guidelines', params);
  };

  getComplianceGuidelineStatus = (guidelineId) => {
    return this.JMPost('/v1/compliance/guidelines/status', {
      guidelineId: guidelineId,
    });
  };

  updateComplianceCriteria = (guidelineId, criteria) => {
    return this.JMPost('/v1/compliance/guidelines/updateCriteria', {
      guidelineId: guidelineId,
      criteria: criteria,
    });
  };

  activateComplianceGuideline = (guidelineId) => {
    return this.JMPost('/v1/compliance/guidelines/activate', {
      guidelineId: guidelineId,
    });
  };

  deactivateComplianceGuideline = (guidelineId = null) => {
    const params = {};
    if (guidelineId) params.guidelineId = guidelineId;
    return this.JMPost('/v1/compliance/guidelines/deactivate', params);
  };

  deleteComplianceGuideline = (guidelineId) =>
    this.JMDelete(`/v1/compliance/guidelines/${guidelineId}`);

  getGroupNoteTypes = () => {
    return this.JMPost('/v1/compliance/guidelines/getGroupNoteTypes', {});
  };

  duplicateCmsGuideline = () => {
    return this.JMPost('/v1/compliance/guidelines/duplicate', {});
  };

  isTimeoutError(error) {
    return (
      error.toString().startsWith('Error: No response') ||
      error.toString().includes('Server Error') ||
      error.toString().includes('Request Timeout')
    );
  }
  // Synapse / Comprehend Voice
  // NOTE: /v1/synapse/key is the new canonical route (Endpoints/Synapse.py);
  // we stay on the legacy /v1/misc/synapse/key until every backend env has
  // the new framework deployed, then flip this path.
  getSynapseKey = (service, deviceInfo = {}) =>
    this.JMPost('/v1/misc/synapse/key', { service, ...deviceInfo });
  // Comprehend Live (ambient agent) — see docs/comprehend-live.md
  getAgentQueue = () => this.JMPost('/v1/synapse/agent/queue', {});
  agentApplyNoteFix = (conversationId, instruction, fieldName = null) =>
    this.JMPost('/v1/synapse/agent/note/applyFix', {
      conversationId: conversationId,
      instruction: instruction,
      fieldName: fieldName,
    });
  // Ambient loop: one call per ~15s VAD chunk — transcribe, brain, commit,
  // actions back (docs/comprehend-live-ambient.md)
  ambientChunk = (payload) => this.JMPost('/v1/synapse/ambient/chunk', payload);
  // MultiRecord++ schedule loader: voice/text/screenshot -> proposed visits
  // (parse only; FE confirms then creates). docs/comprehend-live-v05.md
  parseSchedule = ({ text, audio, image } = {}) =>
    this.JMPost('/v1/synapse/schedule/parse', { text, audio, image });
  // Wakeword Audio Upload Methods for S3 (No Authentication Required)
  getAudioUploadUrl = (filename, label, userName, mimeType = null) => {
    console.log('Getting S3 presigned URL for:', filename);
    const payload = {
      filename: filename,
      label: label,
      userName: userName,
    };
    if (mimeType) {
      payload.mimeType = mimeType;
    }
    return this.JMPostPublic('/v1/wakeword-audio/upload/getUrl', payload);
  };

  deleteAudioFile = (filename, label, userName) => {
    console.log('Deleting audio file:', filename);
    return this.JMPostPublic('/v1/wakeword-audio/delete', {
      filename: filename,
      label: label,
      userName: userName,
    });
  };

  // ChartMogul API Methods
  // Note: These methods use the iTools base URL
  chartMogulCreateSubscription = (payload) => {
    const originalBaseURL = this.apiClient.defaults.baseURL;
    this.apiClient.defaults.baseURL = ITOOLS_BASE_URL;
    try {
      const result = this.JMPost('/chartmogul/webhook', {
        action: 'group_subscription_created',
        ...payload,
      });
      return result;
    } finally {
      this.apiClient.defaults.baseURL = originalBaseURL;
    }
  };

  chartMogulUpdateSubscription = (payload) => {
    const originalBaseURL = this.apiClient.defaults.baseURL;
    this.apiClient.defaults.baseURL = ITOOLS_BASE_URL;
    try {
      const result = this.JMPost('/chartmogul/webhook', {
        action: 'group_subscription_updated',
        ...payload,
      });
      return result;
    } finally {
      this.apiClient.defaults.baseURL = originalBaseURL;
    }
  };

  chartMogulCancelSubscription = (payload) => {
    const originalBaseURL = this.apiClient.defaults.baseURL;
    this.apiClient.defaults.baseURL = ITOOLS_BASE_URL;
    try {
      const result = this.JMPost('/chartmogul/webhook', {
        action: 'group_subscription_deleted',
        ...payload,
      });
      return result;
    } finally {
      this.apiClient.defaults.baseURL = originalBaseURL;
    }
  };

  chartMogulDeleteSubscription = (
    groupSubscriptionId,
    subscriberEmail = null,
  ) => {
    const originalBaseURL = this.apiClient.defaults.baseURL;
    this.apiClient.defaults.baseURL = ITOOLS_BASE_URL;
    try {
      const payload = { group_subscription_id: groupSubscriptionId };
      if (subscriberEmail) {
        payload.subscriber_email = subscriberEmail;
      }
      const result = this.JMPost(
        '/chartmogul/admin/delete_subscription',
        payload,
      );
      return result;
    } finally {
      this.apiClient.defaults.baseURL = originalBaseURL;
    }
  };

  chartMogulSeatIncrease = (payload) => {
    const originalBaseURL = this.apiClient.defaults.baseURL;
    this.apiClient.defaults.baseURL = ITOOLS_BASE_URL;
    try {
      const result = this.JMPost('/chartmogul/webhook', {
        action: 'seat_increase',
        ...payload,
      });
      return result;
    } finally {
      this.apiClient.defaults.baseURL = originalBaseURL;
    }
  };

  chartMogulIssueDueInvoices = (eventDate = null) => {
    const originalBaseURL = this.apiClient.defaults.baseURL;
    this.apiClient.defaults.baseURL = ITOOLS_BASE_URL;
    try {
      const payload = {};
      if (eventDate) {
        payload.event_date = eventDate;
      }
      const result = this.JMPost('/chartmogul/issue_due_invoices', payload);
      return result;
    } finally {
      this.apiClient.defaults.baseURL = originalBaseURL;
    }
  };

  // EMR Directives
  listEMRDirectives = () => this.JMPost('/v1/admin/emr-directives/list', {});

  getEMRDirectiveOptions = (emrName, emrUrl) =>
    this.JMPost('/v1/admin/emr-directives/options', { emrName, emrUrl });

  createEMRDirective = (
    emrName,
    emrUrl,
    noteType,
    section,
    subsection,
    directiveText,
  ) =>
    this.JMPost('/v1/admin/emr-directives/new', {
      emrName,
      emrUrl,
      noteType,
      section,
      subsection,
      directiveText,
    });

  editEMRDirective = (
    directiveId,
    emrName,
    emrUrl,
    noteType,
    section,
    subsection,
    directiveText,
  ) =>
    this.JMPost('/v1/admin/emr-directives/edit', {
      directiveId,
      emrName,
      emrUrl,
      noteType,
      section,
      subsection,
      directiveText,
    });

  deleteEMRDirective = (directiveId, emrName) =>
    this.JMPost('/v1/admin/emr-directives/delete', { directiveId, emrName });

  promoteEMRDirective = (emrName, directiveId = null) =>
    this.JMPost('/v1/admin/emr-directives/promote', {
      emrName,
      ...(directiveId ? { directiveId } : {}),
    });

  listEMRDirectivesProd = () =>
    this.JMPost('/v1/admin/emr-directives/list-prod', {});

  // Transcription Fleet API Methods
  transcriptionStatus = () => {
    const originalBaseURL = this.apiClient.defaults.baseURL;
    this.apiClient.defaults.baseURL = ITOOLS_BASE_URL;
    try {
      return this.JMPost('/transcription/status', {});
    } finally {
      this.apiClient.defaults.baseURL = originalBaseURL;
    }
  };

  transcriptionResize = (desired) => {
    const originalBaseURL = this.apiClient.defaults.baseURL;
    this.apiClient.defaults.baseURL = ITOOLS_BASE_URL;
    try {
      return this.JMPost('/transcription/resize', { desired });
    } finally {
      this.apiClient.defaults.baseURL = originalBaseURL;
    }
  };

  // /transcription/history — time-series of worker count + job stats so the
  // dashboard can show when instances spun up / down across the lookback
  // window (24h default). Workers don't persist after termination, so this is
  // our only window into "what happened earlier today".
  transcriptionHistory = (lookbackMinutes = 1440, periodSeconds = 300) => {
    const originalBaseURL = this.apiClient.defaults.baseURL;
    this.apiClient.defaults.baseURL = ITOOLS_BASE_URL;
    try {
      return this.JMPost('/transcription/history', {
        lookbackMinutes,
        periodSeconds,
      });
    } finally {
      this.apiClient.defaults.baseURL = originalBaseURL;
    }
  };

  // Golden Hour (company snapshot) — iTools /goldenhour/*, dev-gated on the BE.
  // Same base-URL swap as demoFunnel below; the post is issued before the
  // finally restores it, so the request goes to iTools.
  goldenHour = (path, body = {}) => {
    const originalBaseURL = this.apiClient.defaults.baseURL;
    this.apiClient.defaults.baseURL = ITOOLS_BASE_URL;
    try {
      return this.JMPost(`/goldenhour/${path}`, body);
    } finally {
      this.apiClient.defaults.baseURL = originalBaseURL;
    }
  };

  // /demo/funnel — EMR Demo Playground funnel metrics (iTools reads the
  // DEMO_FUNNEL beacon logs the playground's /v1/demo/event writes). Dev-gated
  // on the BE; feeds the Demo Playground panel in the admin dev tools.
  demoFunnel = (lookbackHours = 168) => {
    const originalBaseURL = this.apiClient.defaults.baseURL;
    this.apiClient.defaults.baseURL = ITOOLS_BASE_URL;
    try {
      return this.JMPost('/demo/funnel', { lookbackHours });
    } finally {
      this.apiClient.defaults.baseURL = originalBaseURL;
    }
  };
}

export default ApiClient;
